import React, { useEffect, useMemo, useState } from 'react';
import {
  useGetDashboardSummaryQuery,
  useGetCommonMastersQuery,
  useGetAllDistrictListQuery,
  useGetAllBlockListQuery,
  useGetSchoolListQuery,
  useGetSchoolAbstractQuery,
  useGetSchoolAbstractMultiQuery,
  useGetStudentChildCountAllYearQuery,
  useGetSchoolAlertsQuery,
  useLazyGetSchoolAlertsListQuery,
  SchoolDashboardData,
} from './Dashboard.service';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../../store';
import { setSelection } from '../../store/slices/selectionSlice';
import { useDashboardCacheRollover } from 'shared/hooks/useDataVersion';
import { ENV_CONFIG } from 'shared/config/env.config';
import { buildSsoRedirectUrl } from 'shared/services/ssoService';
import { exportToExcel } from 'shared/export/excel';
import { exportElementToPdf } from 'shared/export/pagePdf';
import { exportTimestamp } from 'shared/export/stamp';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import Filters, { FilterRow } from 'shared/components/Filters/Filters';
import AppChart, {
  ChartSlice,
  ChartSeries,
} from 'shared/components/Chart/AppChart';
import DashboardHeader from 'shared/components/Dashboard/DashboardHeader';
import StatTile from 'shared/components/Dashboard/StatTile';
import MetricCard from 'shared/components/Dashboard/MetricCard';
import ReportDataTable, {
  ReportColumn,
} from 'shared/components/Table/ReportDataTable';
import DistrictMap from 'shared/components/Map/DistrictMap';
import AcademicScores from './AcademicScores';
import Attendance from './Attendance';
import { Dropdown } from 'primereact/dropdown';
import { useSearchParams } from 'react-router-dom';
import { useUI } from '../../shared/UI/Uicontext';
import ReviewDashboard from '../ReviewDashboard/ReviewDashboard';
// Maps the active nav tab to its dashboardsummary component_id. Driven by the
// header selection so the request's component_id is dynamic, not hardcoded.
const COMPONENT_ID_BY_NAV: Record<string, number> = {
  'school-dashboard': 1,
  'academic-scores': 2,
  attendance: 3,
};

// District names shared by every tab's summary table (used for the header
// district dropdown so its value matches a summary row).
const DISTRICT_NAMES = [
  'Tiruvallur',
  'Chennai',
  'Kancheepuram',
  'Chengalpattu',
  'Ranipet',
  'Vellore',
  'Tirupathur',
  'Dharmapuri',
  'Tiruvannamalai',
  'Villupuram',
  'Kallakurichi',
  'Salem',
];
import mockCenters from '../../shared/json/mockCenters.json';
import complianceByPeriod from '../../shared/json/complianceByPeriod.json';
import enrolmentStats from '../../shared/json/enrolmentStats.json';

import geolocation from '../../shared/json/geolocation.json';
import {
  getBlocks,
  getSchools,
  getSchoolDetail,
  getEntityDetail,
  SchoolRow,
  EntityBase,
} from '../../shared/json/reportDrillData';
import {
  drillHint,
  drillSearchPlaceholder,
  scopeLabel,
  withUdise,
} from '../../shared/json/drilldown';
import DrillBreadcrumb from 'shared/components/Dashboard/DrillBreadcrumb';
import SchoolDetailView from './SchoolDetailView';
import EntityDetailView from './EntityDetailView';
import TrendAnalysis from './TrendAnalysis';
import Comparison from './Comparison';
// import Alarming from './Alarming';
import Caution from './Caution';
import Infrastructure from './Infrastructure';
import govtLogo from '../../assets/images/GOVT_LOGO.png';
import './Dashboard.scss';

type Row = [string, string | number];

function group(title: string, rows: Row[]) {
  return { title, rows };
}

// ── Dashboard summary → Numbers-tab mapping ─────────────────────────
// Maps the "School Dashboard" component of the dashboardsummary API onto the
// KPI tiles, secondary metrics and the four charts used by the Numbers sub-tab.

/** Indian short format: 12195166 → "1.22 Cr", 6227112 → "62.27 L". */
const formatShort = (n: number): string => {
  if (n >= 1e7) return `${(n / 1e7).toFixed(2)} Cr`;
  if (n >= 1e5) return `${(n / 1e5).toFixed(2)} L`;
  return n.toLocaleString('en-IN');
};
const formatIndian = (n: number): string => n.toLocaleString('en-IN');

// Coerce a value to a number. The abstract API wraps values as strings
// (Convstr), so numeric strings like "1,234" must be parsed too.
const num = (v: unknown): number => {
  if (typeof v === 'number') return Number.isNaN(v) ? 0 : v;
  if (typeof v === 'string') {
    const n = Number(v.replace(/,/g, '').trim());
    return Number.isNaN(n) ? 0 : n;
  }
  return 0;
};

/** Percentage of `part` within `total`, rounded to 2 dp. */
const asPct = (part: number, total: number): number => {
  if (total <= 0 || part <= 0) return 0;
  const pct = (part / total) * 100;
  const rounded = Math.round(pct * 100) / 100; // 2 dp
  // A genuinely tiny share (e.g. 1 among thousands) rounds to 0.00 at 2 dp and
  // the slice/label vanishes. Keep it non-zero so it still renders.
  if (rounded > 0) return rounded;
  const digits = Math.min(6, Math.ceil(-Math.log10(pct)) + 1);
  const factor = Math.pow(10, digits);
  return Math.ceil(pct * factor) / factor;
};

export interface SchoolDashboardMapped {
  kpi: {
    schools: number;
    students: number;
    boys: number;
    girls: number;
    teachers: number;
  };
  metrics: {
    gpi: string;
    ptr: string;
    girlsShare: string;
    avgStudents: string;
  };
  managementWise: ChartSlice[];
  /** School-type (management) donut: Un-aided / Government / Fully Aided /
   *  Part. Aided / Central Govt — same API counts as managementWise but with
   *  the school-type naming. */
  schoolType: ChartSlice[];
  genderSplit: ChartSlice[];
  socialCategory: ChartSlice[];
  /** Level-of-Education donut: Primary / Upper Primary / Secondary / Higher
   *  Secondary, from the API's own pre-aggregated level totals
   *  (total_primary_students / total_middle_students / total_higher_students /
   *  total_higher_sec_students). */
  levelOfEducation: ChartSlice[];
  enrolmentByGrade: {
    grades: string[];
    boys: number[];
    girls: number[];
    /** Real per-grade transgender count from the API (optional). */
    trans?: number[];
    /** Real per-grade total (boys+girls+trans) from the API. */
    total: number[];
  };
}

/**
 * Parse the School Dashboard `data` into ALL its rows. Handles the object form
 * (single row), a JSON array, and the newline-delimited string form (one row
 * per district, incl. the district_id === 0 state total).
 */
export const parseSchoolDashboardRows = (
  raw: unknown
): SchoolDashboardData[] => {
  if (raw == null) return [];

  if (typeof raw === 'object' && !Array.isArray(raw)) {
    return [raw as SchoolDashboardData];
  }

  if (Array.isArray(raw)) return raw as SchoolDashboardData[];

  if (typeof raw === 'string') {
    const text = raw.trim();
    let parsedWhole: unknown = null;
    try {
      parsedWhole = JSON.parse(text);
    } catch {
      parsedWhole = null;
    }
    if (typeof parsedWhole === 'string') {
      return parseSchoolDashboardRows(parsedWhole); // double-encoded
    }
    if (parsedWhole && typeof parsedWhole === 'object') {
      return (
        Array.isArray(parsedWhole) ? parsedWhole : [parsedWhole]
      ) as SchoolDashboardData[];
    }
    // Newline-delimited objects, possibly wrapped in [ ].
    const inner = text.replace(/^\[/, '').replace(/\]$/, '');
    return inner
      .split(/\r?\n/)
      .map((line) => line.trim().replace(/,$/, ''))
      .filter(Boolean)
      .map((line) => {
        try {
          return JSON.parse(line) as SchoolDashboardData;
        } catch {
          return null;
        }
      })
      .filter((r): r is SchoolDashboardData => r != null);
  }

  return [];
};

export const normalizeSchoolDashboardData = (
  raw: unknown
): SchoolDashboardData | null => {
  const rows = parseSchoolDashboardRows(raw);
  if (rows.length === 0) return null;

  // Prefer the explicit state-total row (district_id === 0).
  const stateRow = rows.find(
    (r) => Number((r as Record<string, unknown>).district_id) === 0
  );
  if (stateRow) return stateRow;

  // Otherwise aggregate every numeric field across all rows.
  const summed: Record<string, number> = {};
  rows.forEach((r) => {
    Object.entries(r as Record<string, unknown>).forEach(([k, v]) => {
      if (k === 'district_id') return;
      if (typeof v === 'number' && !Number.isNaN(v)) {
        summed[k] = (summed[k] ?? 0) + v;
      }
    });
  });
  return summed as unknown as SchoolDashboardData;
};

/**
 * Build everything the Numbers tab needs from the raw School Dashboard payload.
 * Returns null when the component is absent so the caller can fall back to mock.
 */
export const mapSchoolDashboard = (
  data: SchoolDashboardData | null | undefined
): SchoolDashboardMapped | null => {
  if (!data) return null;

  const schools = num(data.total_schools);
  const students = num(data.total_students);
  const boys = num(data.total_boys);
  const girls = num(data.total_girls);

  const trans =
    num(data.total_transgender) ||
    num(data.total_trans) ||
    Math.max(0, students - boys - girls);
  const teachers = num(data.total_teachers);

  const gpi = boys > 0 ? girls / boys : 0;
  const ptr = teachers > 0 ? students / teachers : 0;
  const girlsShare = students > 0 ? (girls / students) * 100 : 0;
  const avgStudents = schools > 0 ? Math.round(students / schools) : 0;
  // Management-Wise donut.
  const mgmt: { label: string; value: number; color: string }[] = [
    {
      label: 'Government',
      value: num(data.total_govt_students),
      color: '#f0803c',
    },
    {
      label: 'Private',
      value: num(data.total_private_students),
      color: '#f2c14e',
    },
    { label: 'Aided', value: num(data.total_aided_students), color: '#26a69a' },
    {
      label: 'Partial Aided',
      value: num(data.total_partial_aided_students),
      color: '#8bc34a',
    },
    {
      label: 'Central Govt',
      value: num(data.total_central_students),
      color: '#c62828',
    },
    {
      label: 'Others',
      value: num(data.total_other_management_students),
      color: '#7e57c2',
    },
  ];
  const mgmtTotal = mgmt.reduce((a, m) => a + m.value, 0);
  const managementWise: ChartSlice[] = mgmt
    .filter((m) => m.value > 0)
    .map((m) => ({
      label: m.label,
      value: asPct(m.value, mgmtTotal),
      color: m.color,
      count: m.value,
    }));

  // School-Type donut — the same API management counts, re-labelled to the
  // school-type naming the UI expects (Un-aided = Private, Fully Aided = Aided,
  // Part. Aided = Partial Aided). Zero buckets are dropped so no "0%" slice
  // appears.
  const stypeBuckets: { label: string; value: number; color: string }[] = [
    {
      label: 'Un-aided',
      value: num(data.total_private_students),
      color: '#f2c14e',
    },
    {
      label: 'Government',
      value: num(data.total_govt_students),
      color: '#f0803c',
    },
    {
      label: 'Fully Aided',
      value: num(data.total_aided_students),
      color: '#8bc34a',
    },
    {
      label: 'Part. Aided',
      value: num(data.total_partial_aided_students),
      color: '#7b1f3a',
    },
    {
      label: 'Central Govt',
      value: num(data.total_central_students),
      color: '#26a69a',
    },
  ];
  const stypeTotal = stypeBuckets.reduce((a, m) => a + m.value, 0);
  const schoolType: ChartSlice[] = stypeBuckets
    .filter((m) => m.value > 0)
    .map((m) => ({
      label: m.label,
      value: asPct(m.value, stypeTotal),
      color: m.color,
      count: m.value,
    }));

  // Gender split donut — Boys, Girls and Transgender (from the API's
  // total_trans). Transgender is ALWAYS shown as its own slice/legend entry,
  // even when a scope's count is 0 (it then reads "0%"), so the category is
  // never hidden.
  const genderSplit: ChartSlice[] = [
    {
      label: 'Boys',
      value: asPct(boys, students),
      color: '#f0803c',
      count: boys,
    },
    {
      label: 'Girls',
      value: asPct(girls, students),
      color: '#26a69a',
      count: girls,
    },
    {
      label: 'Transgender',
      value: asPct(trans, students),
      color: '#7e57c2',
      count: trans,
    },
  ];

  // Social category donut.
  const social: { label: string; value: number; color: string }[] = [
    { label: 'OC', value: num(data.total_oc_students), color: '#f2c14e' },
    { label: 'BC', value: num(data.total_bc_students), color: '#26a69a' },
    { label: 'MBC', value: num(data.total_mbc_students), color: '#8bc34a' },
    { label: 'DNC', value: num(data.total_dnc_students), color: '#c62828' },
    { label: 'SC', value: num(data.total_sc_students), color: '#f0803c' },
    { label: 'ST', value: num(data.total_st_students), color: '#2f5fb0' },
  ];
  const socialTotal = social.reduce((a, x) => a + x.value, 0);
  const socialCategory: ChartSlice[] = social.map((x) => ({
    label: x.label,
    value: asPct(x.value, socialTotal),
    color: x.color,
    count: x.value,
  }));

  // Level-of-Education donut, using the API's own pre-aggregated level
  // totals directly rather than summing per-grade fields ourselves.
  const levelBuckets: { label: string; value: number; color: string }[] = [
    {
      label: 'Primary',
      value: num(data.total_primary_students),
      color: '#f4c430',
    },
    {
      label: 'Upper Primary',
      value: num(data.total_middle_students),
      color: '#f0803c',
    },
    {
      label: 'Secondary',
      value: num(data.total_higher_students),
      color: '#8bc34a',
    },
    {
      label: 'Higher Secondary',
      value: num(data.total_higher_sec_students),
      color: '#26a69a',
    },
  ];
  const levelTotal = levelBuckets.reduce((a, b) => a + b.value, 0);
  const levelOfEducation: ChartSlice[] = levelBuckets
    .filter((b) => b.value > 0)
    .map((b) => ({
      label: b.label,
      value: asPct(b.value, levelTotal),
      color: b.color,
      count: b.value,
    }));

  // Enrolment by grade. Each grade has a combined total key plus (when the API
  // provides them) real boys/girls keys. We read the real split when present
  // and fall back to a single total series otherwise.
  const gradeDefs: {
    label: string;
    key: string;
    boysKey: string;
    girlsKey: string;
  }[] = [
    {
      label: 'Pre-KG',
      key: 'total_pre_kg_students',
      boysKey: 'total_pre_kg_boys',
      girlsKey: 'total_pre_kg_girls',
    },
    {
      label: 'LKG',
      key: 'total_lkg_students',
      boysKey: 'total_lkg_boys',
      girlsKey: 'total_lkg_girls',
    },
    {
      label: 'UKG',
      key: 'total_ukg_students',
      boysKey: 'total_ukg_boys',
      girlsKey: 'total_ukg_girls',
    },
    {
      label: 'I',
      key: 'total_1_students',
      boysKey: 'total_1_boys',
      girlsKey: 'total_1_girls',
    },
    {
      label: 'II',
      key: 'total_2_students',
      boysKey: 'total_2_boys',
      girlsKey: 'total_2_girls',
    },
    {
      label: 'III',
      key: 'total_3_students',
      boysKey: 'total_3_boys',
      girlsKey: 'total_3_girls',
    },
    {
      label: 'IV',
      key: 'total_4_students',
      boysKey: 'total_4_boys',
      girlsKey: 'total_4_girls',
    },
    {
      label: 'V',
      key: 'total_5_students',
      boysKey: 'total_5_boys',
      girlsKey: 'total_5_girls',
    },
    {
      label: 'VI',
      key: 'total_6_students',
      boysKey: 'total_6_boys',
      girlsKey: 'total_6_girls',
    },
    {
      label: 'VII',
      key: 'total_7_students',
      boysKey: 'total_7_boys',
      girlsKey: 'total_7_girls',
    },
    {
      label: 'VIII',
      key: 'total_8_students',
      boysKey: 'total_8_boys',
      girlsKey: 'total_8_girls',
    },
    {
      label: 'IX',
      key: 'total_9_students',
      boysKey: 'total_9_boys',
      girlsKey: 'total_9_girls',
    },
    {
      label: 'X',
      key: 'total_10_students',
      boysKey: 'total_10_boys',
      girlsKey: 'total_10_girls',
    },
    {
      label: 'XI',
      key: 'total_11_students',
      boysKey: 'total_11_boys',
      girlsKey: 'total_11_girls',
    },
    {
      label: 'XII',
      key: 'total_12_students',
      boysKey: 'total_12_boys',
      girlsKey: 'total_12_girls',
    },
  ];
  const grades = gradeDefs.map((g) => g.label);

  // Each grade value in the dashboardsummary / schoolabstractallyear payload is
  // a NESTED object { b, g, t } (e.g. total_1_students: { b, g, t }). Read that
  // split directly; fall back to the older flat form (plain numeric total +
  // optional *_boys / *_girls keys) when present.
  const bag = data as unknown as Record<string, unknown>;
  const gradeCells = gradeDefs.map((g) => {
    const cell = bag[g.key];
    if (cell && typeof cell === 'object') {
      const o = cell as Record<string, unknown>;
      const b = num(o.b);
      const gg = num(o.g);
      const t = num(o.t);
      return { boys: b, girls: gg, trans: t, total: b + gg + t };
    }
    const b = num(bag[g.boysKey]);
    const gg = num(bag[g.girlsKey]);
    const t =
      num(bag[g.boysKey.replace('boys', 'transgender')]) ||
      num(bag[g.boysKey.replace('boys', 'trans')]);
    const total = num(cell);
    return { boys: b, girls: gg, trans: t, total: total || b + gg + t };
  });

  const gradeTotals = gradeCells.map((c) => c.total);
  // Use the real per-grade boys/girls split when any grade carries it;
  // otherwise leave them empty so the chart shows a single "Students" series.
  const hasGradeGender = gradeCells.some((c) => c.boys > 0 || c.girls > 0);
  const gradeBoys = hasGradeGender ? gradeCells.map((c) => c.boys) : [];
  const gradeGirls = hasGradeGender ? gradeCells.map((c) => c.girls) : [];
  const gradeTrans = hasGradeGender ? gradeCells.map((c) => c.trans) : [];

  return {
    kpi: { schools, students, boys, girls, teachers },
    metrics: {
      gpi: gpi.toFixed(2),
      ptr: `${ptr.toFixed(1)} : 1`,
      girlsShare: `${girlsShare.toFixed(1)}%`,
      avgStudents: String(avgStudents),
    },
    managementWise,
    schoolType,
    genderSplit,
    socialCategory,
    levelOfEducation,
    enrolmentByGrade: {
      grades,
      boys: gradeBoys,
      girls: gradeGirls,
      trans: gradeTrans,
      total: gradeTotals,
    },
  };
};

const Dashboard: React.FC = () => {
  const s = useSelector((state: RootState) => state.session);
  const userRoleLevel = s.user_role_level;
  console.log(s, 's');
  console.log(userRoleLevel, 'userRoleLevel');
  const districtLocked =
    userRoleLevel === 'district' || userRoleLevel === 'block';
  const blockLocked = userRoleLevel === 'block';
  // All four tabs read nightly-batch data that is immutable during the day, so
  // the API cache is kept for 24h (see DASHBOARD_CACHE_SECONDS). This watches
  // the data generation stamp and clears that cache the moment a new batch is
  // published, so a long-open tab rolls over instead of showing yesterday's
  // numbers. Mounted once here, covering every tab.
  useDashboardCacheRollover();

  // Academic years for the header dropdown, loaded from the commonmasters API.
  // `id` is the acd_yr the dashboardsummary API expects; `name` is the label.
  const { data: commonMasters } = useGetCommonMastersQuery(undefined, {
    skip: !s.loaded,
  });

  const acdYearRows = useMemo(
    () =>
      (commonMasters?.result?.academic_year ?? []).filter((y) => y?.id != null),
    [commonMasters]
  );

  const currentAcademicStartYear = useMemo(() => {
    const now = new Date();
    return now.getMonth() >= 5 ? now.getFullYear() : now.getFullYear() - 1;
  }, []);

  // Testing-only deep link for Review Dashboard while its nav tab is
  // commented out below (production). Visiting
  // /dashboard?preview=review-dashboard opens straight into it even though
  // there's no tab button for it — the view switch further down keys off
  // activeNav, not off navTabs, so this works regardless of the tab's
  // comment state. Safe to leave in once the tab is uncommented too.
  const [searchParams] = useSearchParams();
  const previewReviewDashboard =
    searchParams.get('preview') === 'review-dashboard';

  // Active nav tab — declared here (ahead of `yearOptions` below, which needs
  // it) because the dashboardsummary query further down also derives its
  // component_id from it.
  const [activeNav, setActiveNav] = useState(
    previewReviewDashboard ? 'review-dashboard' : 'school-dashboard'
  );

  const startYearOf = (name: string): number => {
    const m = String(name).match(/(\d{4})/);
    return m ? Number(m[1]) : NaN;
  };

  // Academic Scores only: the header Year dropdown starts at 2023-2024 (older
  // years predate that tab's data) and its upper bound is the REAL current
  // academic year, computed dynamically from today's date via
  // `currentAcademicStartYear` — not a hardcoded year that would go stale
  // every July. Every other tab keeps the existing fixed 2018-2026 window.
  const isAcademicScoresNav = activeNav === 'academic-scores';

  const yearOptions = useMemo(() => {
    const MIN_START_YEAR = isAcademicScoresNav ? 2024 : 2018; // earliest selectable year.
    const MAX_START_YEAR = isAcademicScoresNav
      ? currentAcademicStartYear
      : 2026; // latest selectable year.
    const seen = new Set<number>();
    const apiYears = acdYearRows
      .filter((y) => {
        const start = startYearOf(y.name);
        if (Number.isNaN(start)) return false;
        // Show only 2018-2019 .. 2025-2026 (drop older and 2026-2027+).
        if (start < MIN_START_YEAR || start > MAX_START_YEAR) return false;
        // Drop duplicate labels (e.g. a repeated 2023-2024 row).
        if (seen.has(start)) return false;
        seen.add(start);
        return true;
      })
      .sort((a, b) => startYearOf(a.name) - startYearOf(b.name))
      .map((y) => ({ label: y.name, value: String(y.id) }));
    if (apiYears.length > 0) return apiYears;

    // Fallback so the header year dropdown is never empty before the API
    // responds: current and previous academic year, value = acd_yr id guess.
    const startY = currentAcademicStartYear;
    return [
      { label: `${startY}-${(startY + 1) % 100}`, value: '2' },
      { label: `${startY - 1}-${startY % 100}`, value: '4' },
    ];
  }, [acdYearRows, currentAcademicStartYear, isAcademicScoresNav]);

  // The acd_yr id the header defaults to on login / reload. The backend's
  // `status = 'in_progress'` flag can lag behind (it currently points at an
  // older year), so we default to the LATEST academic year present — the one
  // with the highest start year parsed from its name (e.g. "2026-2027" → 2026).
  // This guarantees the current year (2026-2027) shows without a hardcoded id.
  const currentYearId = useMemo(() => {
    if (yearOptions.length === 0) return '';
    // Wait for the REAL academic_year list (commonmasters) before auto-selecting
    // a default — otherwise the fallback options briefly drive the header and
    // fire dashboardsummary with a placeholder id (e.g. acd_yr=2) before the
    // real current year (acd_yr=37) is known. No API years yet → select nothing.
    const hasApiYears = acdYearRows.some(
      (y) => !Number.isNaN(startYearOf(y.name))
    );
    if (!hasApiYears) return '';
    // Prefer the academic year the backend flags as current / in-progress
    // (status = 'in_progress' or isactive), so first load lands on the live
    // year (e.g. acd_yr 37 → 2026-2027) rather than guessing. Only honor it
    // when that id is actually one of the selectable options.
    const isCurrentFlag = (y: {
      status?: string | number;
      isactive?: string | number;
    }): boolean => {
      const st = String(y.status ?? '')
        .toLowerCase()
        .replace(/[^a-z]/g, '');
      const act = String(y.isactive ?? '').toLowerCase();
      return (
        st === 'inprogress' ||
        st === 'current' ||
        st === 'active' ||
        act === '1' ||
        act === 'yes' ||
        act === 'true'
      );
    };
    const activeRow = acdYearRows.find((y) => isCurrentFlag(y));
    if (
      activeRow &&
      yearOptions.some((o) => o.value === String(activeRow.id))
    ) {
      return String(activeRow.id);
    }
    // Fallback: the latest academic year present (highest start year).
    return yearOptions.reduce((a, b) =>
      (startYearOf(b.label) || 0) > (startYearOf(a.label) || 0) ? b : a
    ).value;
  }, [yearOptions, acdYearRows]);

  // SLAS and SMC are external portals, not in-app tabs: clicking them hands
  // off to the configured site (REACT_APP_API_slasSiteURL /
  // REACT_APP_API_smcSiteURL via ENV_CONFIG) instead of switching the active
  // nav. Every other tab is an in-app view and just updates `activeNav`.
  const EXTERNAL_NAV_URLS: Record<string, string> = {
    slas: ENV_CONFIG.slasSiteURL,
    smc: ENV_CONFIG.smcSiteURL,
  };
  const handleNavChange = (value: string) => {
    const siteURL = EXTERNAL_NAV_URLS[value];
    if (siteURL) {
      // SSO handoff: encrypt the logged-in username with the SAME passphrase
      // scheme the inbound Angular menu link uses, then redirect to the portal
      // as `<siteURL>/<type><cipher>` so SLAS / SMC can decrypt it and sign the
      // user straight in — mirroring useSsoBootstrap's inbound flow. Prefer the
      // emis_username (what the token exchange uses), falling back to the
      // generic username / localStorage copy.
      const userName =
        s.emis_username || s.username || localStorage.getItem('username') || '';
      const target = userName
        ? buildSsoRedirectUrl(siteURL, userName)
        : siteURL;
      window.open(target);
      return;
    }
    if (value === 'review-dashboard' && !reviewDetailCard) {
      if (activeNav === 'attendance') {
        setReviewDetailCard('Attendance');
      } else if (activeNav === 'infrastructure') {
        setReviewDetailCard('Infrastructure');
      } else if (activeNav === 'academic-scores') {
        setReviewDetailCard('Academic Performance');
      }
    }
    setActiveNav(value);
  };

  // Selected academic year id (header dropdown value). Empty until the year
  // list loads; the effect below selects the current year once available.
  const [headerYear, setHeaderYear] = useState('');
  // True once the user manually changes the year, so the auto-default effect
  // stops overriding their choice.
  const userPickedYearRef = React.useRef(false);

  const handleYearChange = (value: string) => {
    userPickedYearRef.current = true;
    setHeaderYear(value);
  };

  useEffect(() => {
    if (!currentYearId || yearOptions.length === 0) return;
    // Don't override a year the user explicitly selected.
    if (userPickedYearRef.current) return;
    if (headerYear !== currentYearId) {
      setHeaderYear(currentYearId);
    }
  }, [currentYearId, yearOptions, headerYear]);

  // Review Dashboard, Attendance and Infrastructure are frozen to the
  // current academic year only — their Year dropdown shows just that year
  // and is disabled (see `yearDisabled` below).
  const yearFrozenNav =
    activeNav === 'review-dashboard' ||
    activeNav === 'attendance' ||
    activeNav === 'infrastructure';

  // The header Year dropdown's option list while frozen: just the current
  // year, so there is nothing else to pick even before `disabled` kicks in.
  const currentYearOnlyOptions = useMemo(
    () => yearOptions.filter((o) => o.value === currentYearId),
    [yearOptions, currentYearId]
  );

  // The year actually shown/used for data on the CURRENT tab. Deliberately
  // computed rather than written back into `headerYear` — that state is the
  // user's last free pick and needs to survive a trip through a frozen or
  // narrower tab, then reappear when they return to a tab that allows it
  // (e.g. picking 2020-2021 on School Dashboard, visiting Attendance, then
  // coming back to School Dashboard should still show 2020-2021).
  //  • Frozen tabs (Review Dashboard / Attendance / Infrastructure) → always
  //    the current year, regardless of what's picked elsewhere.
  //  • Otherwise, if the picked year isn't one of THIS tab's own options
  //    (e.g. a pre-2023 year picked on School Dashboard, which Academic
  //    Scores doesn't offer) → unselected, rather than silently querying
  //    with a year outside this tab's range.
  //  • Otherwise → the picked year, unchanged.
  const effectiveHeaderYear = useMemo(() => {
    if (yearFrozenNav) return currentYearId || '';
    if (headerYear && yearOptions.length > 0) {
      const stillValid = yearOptions.some((o) => o.value === headerYear);
      if (!stillValid) return '';
    }
    return headerYear;
  }, [yearFrozenNav, currentYearId, headerYear, yearOptions]);

  const selectedAcdYr = effectiveHeaderYear
    ? Number(effectiveHeaderYear)
    : undefined;
  // dashboardsummary is consumed ONLY by the School Dashboard tab (its KPI
  // tiles, charts, map and districts drill). Academic Scores, Attendance and
  // Infrastructure are separate components with their own APIs and never read
  // it — so leave the component_id undefined on those tabs, which keeps the
  // query skipped (see the `selectedComponentId == null` skip below) and stops
  // the needless dashboardsummary call when they're active.
  const selectedComponentId =
    activeNav === 'school-dashboard'
      ? COMPONENT_ID_BY_NAV[activeNav]
      : undefined;

  const selectedYearLabel = useMemo(
    () => yearOptions.find((y) => y.value === effectiveHeaderYear)?.label ?? '',
    [yearOptions, effectiveHeaderYear]
  );

  // Today's date for the header, e.g. "Tuesday, 29 September 2026".
  const todayLabel = useMemo(
    () =>
      new Date().toLocaleDateString('en-GB', {
        // weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      }),
    []
  );

  // Fetch only once both dynamic params are known; RTK Query re-fetches
  // whenever the nav tab or the year changes.
  const { data: dashboardSummary } = useGetDashboardSummaryQuery(
    { component_id: selectedComponentId, acd_yr: selectedAcdYr },
    { skip: !s.loaded || selectedAcdYr == null || selectedComponentId == null }
  );

  // Filters row toggle — declared here so the state-level abstract query below
  // can stay deferred until the user opens the filters. On first page load only
  // `dashboardsummary` should fire; the other data APIs kick in on filter use.
  const [showFilters, setShowFilters] = useState(false);

  // Global filter selections (Management / Category / School Type) + derived
  // ids. Declared HERE (above the abstract query) so the query's skip can key
  // on an actual selection rather than merely the panel being open (#23).
  const [fltProgramme, setFltProgramme] = useState<string | null>(null);
  const [fltManagement, setFltManagement] = useState<string[]>([]);
  const [fltCategory, setFltCategory] = useState<string[]>([]);
  const [fltSchoolType, setFltSchoolType] = useState<string[]>([]);
  const manageId = fltManagement[0] ? Number(fltManagement[0]) : undefined;
  const cateId = fltCategory[0] ? Number(fltCategory[0]) : undefined;
  const schoolTypeId = fltSchoolType[0] ? Number(fltSchoolType[0]) : undefined;
  const hasAbstractFilter =
    manageId != null || cateId != null || schoolTypeId != null;

  // Check if the current tab is Review Dashboard
  const isReviewTab = activeNav === 'review-dashboard';

  // State-level abstract (acd_yr only, no filter). schoolabstractallyear
  // computes the full metric block live — including real per-grade boys/girls
  // (total_N_boys / total_N_girls) that the pre-aggregated dashboardsummary
  // blob lacks. Deferred until the user opens the filter panel so the FIRST
  // page load calls ONLY dashboardsummary; it then enriches the charts.
  // NOTE: gated on an actual filter selection, NOT merely on the filters panel
  // being open. Keying the skip on `showFilters` fired this abstract call (and
  // the loading state) the moment the user clicked the Filter button, before
  // choosing anything (#23). It now fetches only once a filter has a value.
  const { data: stateAbstract } = useGetSchoolAbstractQuery(
    { acd_yr: selectedAcdYr as number },
    { skip: !s.loaded || selectedAcdYr == null || !hasAbstractFilter }
  );

  //console.log(
  // `[dashboardsummary] requesting component_id=${selectedComponentId}&acd_yr=${selectedAcdYr}`
  // );
  //console.log('dashboardSummary------------->', dashboardSummary);

  // Header district + district id lookup, declared here so the filter abstract
  // query below can combine the selected district with the classification.
  const [headerDistrict, setHeaderDistrict] = useState<string | null>(null);
  const { data: allDistricts } = useGetAllDistrictListQuery(undefined, {
    skip: !s.loaded,
  });
  const districtNameToId = useMemo(() => {
    const map = new Map<string, number>();
    (allDistricts?.result?.schooldist ?? []).forEach((d) => {
      if (d?.id != null && d.district_name) {
        map.set(d.district_name, Number(d.id));
      }
    });
    return map;
  }, [allDistricts]);
  // Master id of the currently-selected header district (headerDistrict is a name).
  const selectedDistrictId = headerDistrict
    ? (districtNameToId.get(headerDistrict) ??
      (districtLocked && s.districtId ? Number(s.districtId) : undefined))
    : districtLocked && s.districtId
      ? Number(s.districtId)
      : undefined;

  // Map the "School Dashboard" component onto the Numbers tab (KPI tiles,
  // metrics, and the four charts). Null until the API returns → mock fallback.
  // (Filter selections fltManagement/fltCategory/fltSchoolType + manageId/
  // cateId/schoolTypeId + hasAbstractFilter are declared earlier, above the
  // abstract query that depends on them.)

  // Use the geo endpoint (schoolabstractgeo) when a filter is multi-select OR
  // combined with a selected district — it takes district_id + classification
  // lists together (e.g. "Adi-Dravida Welfare" within a chosen district).
  const isMultiFilter =
    fltManagement.length > 1 ||
    fltCategory.length > 1 ||
    fltSchoolType.length > 1;
  const useGeoAbstract =
    hasAbstractFilter && (isMultiFilter || selectedDistrictId != null);

  // Geo abstract: district_id (if selected) + classification lists.
  const { data: schoolAbstractMulti } = useGetSchoolAbstractMultiQuery(
    {
      acd_yr: selectedAcdYr as number,
      ...(selectedDistrictId != null && { district_id: selectedDistrictId }),
      ...(fltManagement.length > 0 && { manage_id: fltManagement.join(',') }),
      ...(fltCategory.length > 0 && { cate_id: fltCategory.join(',') }),
      ...(fltSchoolType.length > 0 && {
        school_type_id: fltSchoolType.join(','),
      }),
    },
    { skip: !s.loaded || selectedAcdYr == null || !useGeoAbstract }
  );

  // State-level single-filter abstract (no district): all figures under e.g.
  // "Adi-Dravida Welfare" across Tamil Nadu.
  const { data: schoolAbstractSingle } = useGetSchoolAbstractQuery(
    {
      acd_yr: selectedAcdYr as number,
      ...(manageId != null && { manage_id: manageId }),
      ...(cateId != null && { cate_id: cateId }),
      ...(schoolTypeId != null && { school_type_id: schoolTypeId }),
    },
    {
      skip:
        !s.loaded ||
        selectedAcdYr == null ||
        !hasAbstractFilter ||
        useGeoAbstract,
    }
  );

  // Prefer the geo (district + classification) result when active, else single.
  const schoolAbstract = useGeoAbstract
    ? schoolAbstractMulti
    : schoolAbstractSingle;

  // Log the scoped abstract so we can confirm the filtered figures.
  useEffect(() => {
    if (schoolAbstract) {
      //console.log(
      //   `[schoolabstractallyear] manage_id=${manageId} cate_id=${cateId} school_type_id=${schoolTypeId} →`,
      //   schoolAbstract?.result?.summary
      // );
    }
  }, [schoolAbstract, manageId, cateId, schoolTypeId]);

  const schoolDash = useMemo(() => {
    // When a filter is active, use the scoped abstract summary so all cards
    // and charts reflect the selected management/category/school-type.
    const abstractSummary = schoolAbstract?.result?.summary;
    if (hasAbstractFilter && abstractSummary) {
      return mapSchoolDashboard(abstractSummary as SchoolDashboardData);
    }

    // Otherwise the unfiltered School Dashboard component. `data` may be an
    // object (state total) or a newline-delimited string → normalise it.
    const result = dashboardSummary?.result ?? {};
    // Find the School Dashboard component by a flexible, case-insensitive key
    // match (handles "School Dashboard", "school_dashboard", etc.).
    const key = Object.keys(result).find(
      (k) => k.toLowerCase().replace(/[^a-z]/g, '') === 'schooldashboard'
    );
    const comp = (
      key ? (result as Record<string, unknown>)[key] : undefined
    ) as { data?: unknown } | undefined;
    const raw = comp?.data ?? comp;
    const normalized = normalizeSchoolDashboardData(raw);
    return mapSchoolDashboard(normalized);
  }, [dashboardSummary, schoolAbstract, hasAbstractFilter]);

  // Log the full API response and the mapped values for inspection.
  useEffect(() => {
    if (!dashboardSummary) return;
    //console.log('[dashboardsummary] full response:', dashboardSummary);
    //console.log(
    //   '[dashboardsummary] result keys:',
    //   Object.keys(dashboardSummary.result ?? {})
    // );
    const sd = dashboardSummary.result?.['School Dashboard']?.data;
    //console.log('[dashboardsummary] School Dashboard data:', sd);
    if (schoolDash) {
      //console.log('[dashboardsummary] KPI:', schoolDash.kpi);
      //console.log('[dashboardsummary] Metrics:', schoolDash.metrics);
      //console.log(
      //   '[dashboardsummary] Management-Wise:',
      //   schoolDash.managementWise
      // );
      //console.log('[dashboardsummary] Gender Split:', schoolDash.genderSplit);
      //console.log(
      //   '[dashboardsummary] Social Category:',
      //   schoolDash.socialCategory
      // );
      //console.log(
      //   '[dashboardsummary] Enrolment by Grade:',
      //   schoolDash.enrolmentByGrade
      // );
    }
  }, [dashboardSummary, schoolDash]);

  // Dataset we filter on — the full 60-record mock JSON, so every row is shown.
  const reportRows: FilterRow[] = mockCenters;

  // Rows currently visible after applying the Filters selection.
  // Initialised from the full dataset; the Filters callback narrows it.
  const [filteredRows, setFilteredRows] = useState<FilterRow[]>(reportRows);

  // Management-Wise doughnut view selector + the dataset each option maps to.
  const managementViews = [
    // { label: 'Management-Wise', value: 'management' },
    { label: 'Level of Education', value: 'level' },
    { label: 'School Type', value: 'schoolType' },
    // { label: 'Social Category', value: 'social' },
    // { label: 'Gender', value: 'gender' },
  ];
  const [managementView, setManagementView] = useState('level');

  // Level-of-Education split read STRAIGHT from the dashboardsummary
  // state-total row (district_id === 0), using the API's own pre-aggregated
  // level totals (total_primary_students / total_middle_students /
  // total_higher_students / total_higher_sec_students) instead of summing
  // per-grade fields ourselves. Replaces the static enrolmentStats.json mock,
  // whose hardcoded percentages (38/24/22/16) didn't match this state's real
  // split.
  const summaryLevelOfEducation = useMemo<ChartSlice[] | null>(() => {
    const rows = parseSchoolDashboardRows(
      dashboardSummary?.result?.['School Dashboard']?.data
    );
    if (rows.length === 0) return null;
    const row = rows.find(
      (r) => Number((r as Record<string, unknown>).district_id) === 0
    ) as Record<string, unknown> | undefined;
    if (!row) return null;

    const buckets = [
      {
        label: 'Primary',
        count: num(row.total_primary_students),
        color: '#f4c430',
      },
      {
        label: 'Upper Primary',
        count: num(row.total_middle_students),
        color: '#f0803c',
      },
      {
        label: 'Secondary',
        count: num(row.total_higher_students),
        color: '#8bc34a',
      },
      {
        label: 'Higher Secondary',
        count: num(row.total_higher_sec_students),
        color: '#26a69a',
      },
    ];
    const total = buckets.reduce((a, b) => a + b.count, 0);
    if (total === 0) return null;
    return buckets
      .filter((b) => b.count > 0)
      .map((b) => ({
        label: b.label,
        value: asPct(b.count, total),
        color: b.color,
        count: b.count,
      }));
  }, [dashboardSummary]);

  const managementDatasets: Record<string, ChartSlice[]> = {
    // API data only — no dummy fallback. Empty until the API responds.
    management: schoolDash?.managementWise ?? [],
    // Level of Education: on first load (no filter) read straight from the
    // dashboardsummary state-total row's own level fields; a filter/drill
    // then switches to the scoped API value (see managementSlices below).
    level:
      !hasAbstractFilter && summaryLevelOfEducation
        ? summaryLevelOfEducation
        : (schoolDash?.levelOfEducation ?? []),
    // School-Type → real API management breakdown (Un-aided / Government /
    // Fully Aided / Part. Aided / Central Govt), no longer the static mock.
    schoolType: schoolDash?.schoolType ?? [],
    social: schoolDash?.socialCategory ?? [],
    gender: schoolDash?.genderSplit ?? [],
  };

  // Gender Split card — its own independent selector + datasets.
  const genderViews = [
    { label: 'Gender', value: 'gender' },
    { label: 'By Management', value: 'management' },
    { label: 'By Level', value: 'level' },
  ];
  const [genderView, setGenderView] = useState('gender');

  const summaryGenderSplit = useMemo<ChartSlice[] | null>(() => {
    const rows = parseSchoolDashboardRows(
      dashboardSummary?.result?.['School Dashboard']?.data
    );
    if (rows.length === 0) return null;
    const row = rows.find(
      (r) => Number((r as Record<string, unknown>).district_id) === 0
    ) as Record<string, unknown> | undefined;
    if (!row) return null;
    const boys = num(row.total_boys);
    const girls = num(row.total_girls);
    const trans = num(row.total_transgender);
    const total = boys + girls + trans;
    if (total === 0) return null;
    return [
      {
        label: 'Boys',
        value: asPct(boys, total),
        color: '#f0803c',
        count: boys,
      },
      {
        label: 'Girls',
        value: asPct(girls, total),
        color: '#26a69a',
        count: girls,
      },
      {
        label: 'Transgender',
        value: asPct(trans, total),
        color: '#7e57c2',
        count: trans,
      },
    ];
  }, [dashboardSummary]);

  // Level-of-Education split read STRAIGHT from the dashboardsummary
  // state-total row (district_id === 0), using the API's own pre-aggregated
  // Management-Wise split read STRAIGHT from the dashboardsummary state-total
  // row (district_id === 0) on first load, using the exact management fields —
  // total_govt_students / total_private_students / total_aided_students /
  // total_partial_aided_students / total_central_students. Guarantees the
  // first render shows these dashboardsummary counts; a filter/drill then
  // switches to the scoped API value (see managementSlices below).
  const summaryManagementWise = useMemo<ChartSlice[] | null>(() => {
    const rows = parseSchoolDashboardRows(
      dashboardSummary?.result?.['School Dashboard']?.data
    );
    if (rows.length === 0) return null;
    const row = rows.find(
      (r) => Number((r as Record<string, unknown>).district_id) === 0
    ) as Record<string, unknown> | undefined;
    if (!row) return null;
    const mgmt = [
      {
        label: 'Government',
        value: num(row.total_govt_students),
        color: '#f0803c',
      },
      {
        label: 'Private',
        value: num(row.total_private_students),
        color: '#f2c14e',
      },
      {
        label: 'Aided',
        value: num(row.total_aided_students),
        color: '#26a69a',
      },
      {
        label: 'Partial Aided',
        value: num(row.total_partial_aided_students),
        color: '#8bc34a',
      },
      {
        label: 'Central Govt',
        value: num(row.total_central_students),
        color: '#c62828',
      },
      {
        label: 'Others',
        value: num(row.total_other_management_students),
        color: '#7e57c2',
      },
    ];
    const total = mgmt.reduce((a, m) => a + m.value, 0);
    if (total === 0) return null;
    return mgmt
      .filter((m) => m.value > 0)
      .map((m) => ({
        label: m.label,
        value: asPct(m.value, total),
        color: m.color,
        count: m.value,
      }));
  }, [dashboardSummary]);

  const genderDatasets: Record<string, ChartSlice[]> = {
    // Gender: on first load (no filter) read the split straight from the
    // dashboardsummary state-total row (real total_transgender); when a filter
    // is active fall back to the scoped schoolDash split.
    gender:
      !hasAbstractFilter && summaryGenderSplit
        ? summaryGenderSplit
        : (schoolDash?.genderSplit ?? []),
    management: schoolDash?.managementWise ?? [],
    level:
      !hasAbstractFilter && summaryLevelOfEducation
        ? summaryLevelOfEducation
        : (schoolDash?.levelOfEducation ?? []),
  };

  // Social Category card — its own independent selector + datasets.
  const socialViews = [
    { label: 'Social Category', value: 'social' },
    { label: 'By Gender', value: 'gender' },
    { label: 'By Management', value: 'management' },
  ];
  const [socialView, setSocialView] = useState('social');
  const socialDatasets: Record<string, ChartSlice[]> = {
    // API data only — no dummy fallback.
    social: schoolDash?.socialCategory ?? [],
    gender: schoolDash?.genderSplit ?? [],
    management: schoolDash?.managementWise ?? [],
  };

  // Dashboard header state (mock, prop-driven). headerYear and activeNav are
  // declared earlier (above the dashboardsummary query, which depends on them).
  const [activeSubTab, setActiveSubTab] = useState('numbers');
  const [headerSource, setHeaderSource] = useState('emis');
  const [cardInPalliPaarvai, setCardInPalliPaarvai] = useState('');
  // Palli Paarvai's currently active tab (1-6), reported up by
  // ReviewDashboard/PalliPaarvaiDetail — used below to hide the header's
  // Category filter specifically on % Class Observation (1) / % Officials
  // Not Observed (3), where category doesn't apply to the data.
  const [palliPaarvaiViewId, setPalliPaarvaiViewId] = useState<number | null>(
    null
  );
  // On Palli Paarvai's % Class Observation (1) / % Officials Not Observed (3)
  // tabs, the classification dims (Category / School Type / Management) don't
  // apply — the official data (pp_dashboard_users_list_monthwise) has no such
  // columns. Rather than hide them, show all three but FREEZE them (visible
  // for context, non-interactive) so it's clear they aren't filtering here.
  const freezeClassFilters =
    cardInPalliPaarvai === 'Palli Paarvai' &&
    (palliPaarvaiViewId === 1 || palliPaarvaiViewId === 3);

  // When the freeze turns on (landing on tab 1/3), drop any Category / School
  // Type / Management selection carried over from another tab — otherwise the
  // frozen dropdowns would display a stale selection the user can't clear and
  // that isn't actually filtering this tab's data.
  useEffect(() => {
    if (!freezeClassFilters) return;
    if (fltCategory.length) setFltCategory([]);
    if (fltSchoolType.length) setFltSchoolType([]);
    if (fltManagement.length) setFltManagement([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [freezeClassFilters]);
  // headerDistrict + allDistricts + districtNameToId + selectedDistrictId are
  // declared earlier (above the filter abstract query that uses them).
  const [headerBlock, setHeaderBlock] = useState<string | null>(null);
  const [headerSchool, setHeaderSchool] = useState<string | null>(null);
  const [reviewDetailCard, setReviewDetailCard] = useState<string | null>(null);
  // Review Dashboard "components" view = the cards grid itself, with no card
  // drilled into. The main School dropdown has no effect there (the component
  // cards aren't school-scoped), so while this view is shown the School
  // dropdown is cleared and frozen — see the clear effect below and
  // `schoolDisabled` on the header.
  const reviewComponentsView =
    activeNav === 'review-dashboard' && !reviewDetailCard;
  useEffect(() => {
    if (cardInPalliPaarvai === 'Palli Paarvai' || reviewComponentsView) {
      setHeaderSchool(null);
      setSelectedSchool(null);
    }
  }, [cardInPalliPaarvai, reviewComponentsView]);
  const REVIEW_CARD_TO_NAV: Record<string, string> = {
    Attendance: 'attendance',
    Infrastructure: 'infrastructure',
    'Academic Performance': 'academic-scores',
  };

  const handleReviewSchoolSelect = (schoolInfo: {
    name: string;
    udise?: string;
    cardTitle: string;
  }) => {
    setReviewDetailCard(schoolInfo.cardTitle);
    handleSchoolChange(schoolInfo.name);

    const targetNav = REVIEW_CARD_TO_NAV[schoolInfo.cardTitle];
    if (targetNav) {
      setActiveNav(targetNav);
    }
  };

  const districtOptions = useMemo(() => {
    const rows = allDistricts?.result?.schooldist ?? [];
    if (rows.length > 0) {
      return rows
        .filter((d) => d?.district_name)
        .map((d) => ({ label: d.district_name, value: d.district_name }));
    }
    return DISTRICT_NAMES.map((name) => ({ label: name, value: name }));
  }, [allDistricts]);

  // Blocks for the selected district, from the API. Skipped until a district
  // with a known id is selected.
  const { data: blockList } = useGetAllBlockListQuery(
    { district_id: selectedDistrictId as number },
    { skip: !s.loaded || selectedDistrictId == null }
  );

  // Block dropdown options (value === block name, so drill/summary matching by
  // name keeps working). API values only — empty until the API responds.
  const blockRows = useMemo(() => {
    const r = blockList?.result;
    return r?.schoolblock ?? r?.block ?? r?.blocks ?? [];
  }, [blockList]);

  const blockOptions = useMemo(
    () =>
      blockRows
        .filter((b) => b?.block_name)
        .map((b) => ({ label: b.block_name, value: b.block_name })),
    [blockRows]
  );

  // block name → id, so the selected header block can be sent as block_id.
  const blockNameToId = useMemo(() => {
    const map = new Map<string, number>();
    blockRows.forEach((b) => {
      if (b?.id != null && b.block_name) map.set(b.block_name, Number(b.id));
    });
    return map;
  }, [blockRows]);

  // The id of the currently-selected header block (headerBlock is a name).
  const selectedBlockId = headerBlock
    ? (blockNameToId.get(headerBlock) ??
      (blockLocked && s.blockId ? Number(s.blockId) : undefined))
    : blockLocked && s.blockId
      ? Number(s.blockId)
      : undefined;

  // Schools for the selected block + academic year, from the API. Skipped
  // until a block with a known id and a year are both selected.
  // Fetch the block's schools ONCE (keyed only by block + year). Classification
  // filters are applied client-side below, so changing them never refetches.
  const { data: schoolList, isFetching: schoolListFetching } =
    useGetSchoolListQuery(
      {
        acd_yr: selectedAcdYr as number,
        block_id: selectedBlockId as number,
      },
      { skip: !s.loaded || selectedBlockId == null || selectedAcdYr == null }
    );

  // Academic Scores and Attendance are scoped to Government / Partially
  // Aided / Fully Aided schools only (their data excludes Un-aided, Central
  // Govt, etc.) — so on those two tabs only, the School dropdown (and the
  // School Type / Management filter dropdowns further below) must only
  // offer those three manage_cate ids (1, 2, 4), not the full master list
  // every other tab (School Dashboard, Review Dashboard, Infrastructure)
  // shows. Declared here, ahead of `schoolOptions`, so that dropdown can use
  // it too.
  // Palli Paarvai (a Review Dashboard card) shares the same scope as Academic
  // Scores / Attendance — Government / Partially Aided / Fully Aided only — so
  // its School Type and Management filters offer the same three manage_cate
  // ids (1, 2, 4) rather than the full master list.
  const restrictSchoolTypes =
    activeNav === 'academic-scores' ||
    activeNav === 'attendance' ||
    cardInPalliPaarvai === 'Palli Paarvai';
  const ALLOWED_SCHOOL_TYPE_IDS = new Set([1, 2, 4]);

  // Raw management rows (unfiltered by the current School Type selection),
  // so a management → school-type lookup always has every row available
  // regardless of what's currently picked in the School Type filter.
  const managementRows = useMemo(() => {
    const result = (commonMasters?.result ?? {}) as Record<string, unknown>;
    return (
      Array.isArray(result.management) ? result.management : []
    ) as Record<string, unknown>[];
  }, [commonMasters]);

  // Management id → its manage_cate_id (School Type) — used both for the
  // reverse Management → School-Type sync further below, and here to resolve
  // each schoollist row's own manage_cate bucket (the schoollist API gives a
  // school's `manage_id`, not its manage_cate, directly).
  const managementIdToCateId = useMemo(() => {
    const map = new Map<string, string>();
    managementRows.forEach((row) => {
      if (row.id != null && row.mana_cate_id != null) {
        map.set(String(row.id), String(row.mana_cate_id));
      }
    });
    return map;
  }, [managementRows]);

  // School dropdown options (value === school name). API values only.
  const schoolOptions = useMemo(() => {
    const r = schoolList?.result;
    const rows = (r?.school ?? r?.schools ?? r?.schoollist ?? []) as Record<
      string,
      unknown
    >[];
    const mSel = new Set(fltManagement.map(String));
    const cSel = new Set(fltCategory.map(String));
    const tSel = new Set(fltSchoolType.map(String));
    return (
      rows
        .filter((sc) => sc?.school_name)
        .filter((sc) => mSel.size === 0 || mSel.has(String(sc.manage_id)))
        .filter((sc) => cSel.size === 0 || cSel.has(String(sc.cate_id)))
        .filter((sc) => tSel.size === 0 || tSel.has(String(sc.school_type_id)))
        // Academic Scores / Attendance only: drop any school whose management
        // doesn't resolve to the Government / Partially Aided / Fully Aided
        // manage_cate ids (1, 2, 4) — e.g. Un-aided/private and Central Govt
        // schools, which those two tabs' data excludes entirely.
        .filter(
          (sc) =>
            !restrictSchoolTypes ||
            ALLOWED_SCHOOL_TYPE_IDS.has(
              Number(managementIdToCateId.get(String(sc.manage_id)))
            )
        )
        .map((sc) => ({
          // Label shows the UDISE for identification; VALUE stays the bare
          // school_name because every selection/match (headerSchool,
          // selectedSchoolId, the drill-grid row-click round-trip) keys off it.
          label: withUdise(String(sc.school_name), String(sc.udise_code ?? '')),
          value: String(sc.school_name),
        }))
    );
  }, [
    schoolList,
    fltManagement,
    fltCategory,
    fltSchoolType,
    restrictSchoolTypes,
    managementIdToCateId,
  ]);

  // ── Report drill-down state (Districts → Blocks → Schools). Declared here
  // so the header dropdown handlers below can keep the table drill in sync. ──
  const [drillDistrict, setDrillDistrict] = useState<string | null>(null);
  const [drillBlock, setDrillBlock] = useState<string | null>(null);
  const [selectedSchool, setSelectedSchool] = useState<SchoolRow | null>(null);
  const [drillDistrictId, setDrillDistrictId] = useState<number | null>(null);
  const [drillBlockId, setDrillBlockId] = useState<number | null>(null);

  // Caution-tab override for the header Block / School dropdowns: only the
  // blocks/schools that have caution records for the selected district. Set by
  // the Caution component while it's mounted; null (→ normal full lists)
  // otherwise, so switching away from the caution tab reverts the dropdowns.
  const [cautionScope, setCautionScope] = useState<{
    blocks: { label: string; value: string }[];
    schools: { label: string; value: string }[];
  } | null>(null);

  // Changing district resets block + school. Keeps the header dropdowns AND
  // the report table drill in sync (selecting a district here drills the table
  // to that district's blocks).
  const handleDistrictChange = (value: string) => {
    if (districtLocked) return;
    setHeaderDistrict(value || null);
    setHeaderBlock(null);
    setHeaderSchool(null);
    // Sync the table drill.
    setSelectedSchool(null);
    setDrillBlock(null);
    setDrillBlockId(null);
    if (value) {
      setDrillDistrict(value);
      setDrillDistrictId(districtNameToId.get(value) ?? null);
    } else {
      setDrillDistrict(null);
      setDrillDistrictId(null);
    }
  };
  const handleBlockChange = (value: string) => {
    if (blockLocked) return;
    setHeaderBlock(value || null);
    setHeaderSchool(null);
    // Sync the table drill to the selected block.
    setSelectedSchool(null);
    if (value) {
      setDrillBlock(value);
      setDrillBlockId(blockNameToId.get(value) ?? null);
    } else {
      setDrillBlock(null);
      setDrillBlockId(null);
    }
  };

  // ── Role-based geography pre-selection & locking ─────────────────────────
  // District users: lock to their assigned district.
  // Block users: lock to their assigned district and block.
  // State users: full statewide access (no locking).
  useEffect(() => {
    if (!s.loaded || !districtLocked) return;
    if (s.districtName || s.districtId) {
      const distList = allDistricts?.result?.schooldist ?? [];
      const match = distList.find(
        (d) =>
          (s.districtId && String(d.id) == String(s.districtId)) ||
          (s.districtName &&
            d.district_name?.trim().toLowerCase() ===
              s.districtName.trim().toLowerCase())
      );
      const name = match?.district_name || s.districtName;
      const id =
        match?.id != null
          ? Number(match.id)
          : s.districtId
            ? Number(s.districtId)
            : null;

      if (name && headerDistrict !== name) {
        setHeaderDistrict(name);
        setDrillDistrict(name);
        if (id != null) {
          setDrillDistrictId(id);
        }
      } else if (id != null && drillDistrictId !== id) {
        setDrillDistrictId(id);
      }
    }
  }, [
    s.loaded,
    districtLocked,
    s.districtName,
    s.districtId,
    allDistricts,
    headerDistrict,
    drillDistrictId,
  ]);

  useEffect(() => {
    if (!s.loaded || !blockLocked) return;
    if (s.blockName || s.blockId) {
      const match = blockRows.find(
        (b) =>
          (s.blockId && String(b.id) == String(s.blockId)) ||
          (s.blockName &&
            b.block_name?.trim().toLowerCase() ===
              s.blockName.trim().toLowerCase())
      );
      const name = match?.block_name || s.blockName;
      const id =
        match?.id != null
          ? Number(match.id)
          : s.blockId
            ? Number(s.blockId)
            : null;

      if (name && headerBlock !== name) {
        setHeaderBlock(name);
        setDrillBlock(name);
        if (id != null) {
          setDrillBlockId(id);
        }
      } else if (id != null && drillBlockId !== id) {
        setDrillBlockId(id);
      }
    }
  }, [
    s.loaded,
    blockLocked,
    s.blockName,
    s.blockId,
    blockRows,
    headerBlock,
    drillBlockId,
  ]);

  // Selecting a school in the header opens its detail view (same as clicking a
  // school row in the report table). Resolves the school_id from the loaded
  // school list so the detail can match the API row.
  const handleSchoolChange = (value: string | null) => {
    setHeaderSchool(value || null);
    if (!value) {
      setSelectedSchool(null);
      return;
    }
    const rows =
      schoolList?.result?.school ??
      schoolList?.result?.schools ??
      schoolList?.result?.schoollist ??
      [];
    const match = rows.find((sc) => String(sc.school_name ?? '') === value);
    // Carry the UDISE code too so schoolDetail can fall back to matching the
    // API row by UDISE when the id spaces differ between schoollist and the
    // district child-count rows.
    setSelectedSchool({
      name: value,
      school_id: Number(match?.id ?? 0),
      udise_code: match?.udise_code ?? '',
    } as unknown as SchoolRow);

    // If on Review Dashboard and one of the three cards is active, redirect to that main card!
    if (activeNav === 'review-dashboard' && reviewDetailCard) {
      const targetNav = REVIEW_CARD_TO_NAV[reviewDetailCard];
      if (targetNav) {
        setActiveNav(targetNav);
      }
    }
  };

  // Jump straight to a geography, setting every level at once (e.g. clicking a
  // flagged school in Caution, whose row already names its district + block).
  //
  // This deliberately does NOT reuse handleDistrictChange → handleBlockChange
  // → handleSchoolChange: each of those resets its children, so calling them
  // in sequence would wipe the block and school back to null. The *Id lookups
  // derive from these names and fill themselves in as the block / school lists
  // for the new scope arrive.
  const handleGeoSelect = (geo: {
    district?: string | null;
    block?: string | null;
    school?: string | null;
  }) => {
    const nextDistrict = districtLocked ? headerDistrict : geo.district || null;
    const nextBlock = blockLocked ? headerBlock : geo.block || null;
    setHeaderDistrict(nextDistrict);
    setHeaderBlock(nextBlock);
    setHeaderSchool(geo.school || null);
    // Keep the report table's drill state pointing at the same place.
    setDrillDistrict(nextDistrict);
    setDrillDistrictId(
      nextDistrict
        ? (districtNameToId.get(nextDistrict) ??
            (districtLocked && s.districtId ? Number(s.districtId) : null))
        : null
    );
    setDrillBlock(nextBlock);
    setDrillBlockId(
      nextBlock
        ? (blockNameToId.get(nextBlock) ??
            (blockLocked && s.blockId ? Number(s.blockId) : null))
        : null
    );
    setSelectedSchool(null);
  };

  // Keep the header District / Block dropdowns in sync with the Review
  // Dashboard grid drill. The grid's names come from the review* APIs (often
  // UPPERCASE), so each is resolved case-insensitively to the dropdown's own
  // option value before being applied; unmatched names fall back to the raw
  // grid name so the selection still registers.
  const handleReviewGeoSync = (geo: {
    district: string | null;
    block: string | null;
  }) => {
    const norm = (v: string) => v.trim().toUpperCase();
    const canonDistrict = geo.district
      ? (districtOptions.find((o) => norm(o.value) === norm(geo.district!))
          ?.value ?? geo.district)
      : null;
    if (!canonDistrict) {
      // Grid returned to the all-districts level → clear the whole trail.
      if (!districtLocked) {
        handleDistrictChange('');
      }
      return;
    }
    const canonBlock = geo.block
      ? (blockOptions.find((o) => norm(o.value) === norm(geo.block!))?.value ??
        geo.block)
      : null;
    const sameDistrict = norm(headerDistrict ?? '') === norm(canonDistrict);
    if (!canonBlock) {
      // District-level selection (or stepping back up from a block).
      if (!sameDistrict && !districtLocked) {
        handleDistrictChange(canonDistrict);
      } else if (headerBlock && !blockLocked) {
        handleBlockChange('');
      }
      return;
    }
    // Block-level selection. When the district is already current (the usual
    // sequential drill) just set the block; otherwise set both at once so the
    // ids fill in as the new scope's lists arrive.
    if (sameDistrict) {
      if (!blockLocked) handleBlockChange(canonBlock);
    } else {
      handleGeoSelect({
        district: districtLocked ? headerDistrict : canonDistrict,
        block: blockLocked ? headerBlock : canonBlock,
        school: null,
      });
    }
  };

  // Id of the currently-selected header school (headerSchool is a name).
  const selectedSchoolId = useMemo(() => {
    if (!headerSchool) return undefined;
    const rows =
      schoolList?.result?.school ??
      schoolList?.result?.schools ??
      schoolList?.result?.schoollist ??
      [];
    const match = rows.find(
      (sc) => String(sc.school_name ?? '') === headerSchool
    ) as Record<string, unknown> | undefined;
    // The schoollist row's id field is `school_id` (NOT `id`), so the old
    // `match.id` lookup always came back undefined and school scope was never
    // sent to the attendance / trend APIs. Accept the common variants.
    const rawId =
      match?.id ?? match?.school_id ?? match?.schoolId ?? match?.schoolid;
    return rawId != null && rawId !== '' ? Number(rawId) : undefined;
  }, [headerSchool, schoolList]);

  // Keep the header School dropdown's value consistent with its options.
  // A school carried over from another context (e.g. Palli Paarvai, whose
  // school_name strings come from a different API and may differ in
  // case/whitespace — or not exist at all in the current block's list) would
  // otherwise leave the dropdown showing a blank/placeholder (the value
  // matches no option) AND break selectedSchoolId (exact-name lookup fails),
  // so no school scope reached the APIs. When it still matches by name
  // (ignoring case/whitespace) snap it to the canonical option value so the
  // dropdown shows it; when it's genuinely absent, clear it so the view
  // falls back to block-level data instead of a stuck, empty dropdown.
  // Skipped while a Caution-style view owns the scope (cautionScope set —
  // those views reconcile their own stale school), and while the school list
  // is still loading / empty (so a loading window never wrongly clears).
  useEffect(() => {
    if (!headerSchool || cautionScope) return;
    if (schoolListFetching || schoolOptions.length === 0) return;
    const norm = (v: string) => v.trim().toLowerCase();
    const target = norm(headerSchool);
    const match = schoolOptions.find((o) => norm(o.value) === target);
    if (!match) {
      handleSchoolChange(null);
    } else if (match.value !== headerSchool) {
      handleSchoolChange(match.value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headerSchool, cautionScope, schoolListFetching, schoolOptions]);

  // Keep the global selection store in sync so every tab can read the ids.
  const dispatch = useDispatch<AppDispatch>();
  useEffect(() => {
    dispatch(
      setSelection({
        districtId: selectedDistrictId ?? null,
        blockId: selectedBlockId ?? null,
        schoolId: selectedSchoolId ?? null,
        acdYr: selectedAcdYr ?? null,
      })
    );
  }, [
    dispatch,
    selectedDistrictId,
    selectedBlockId,
    selectedSchoolId,
    selectedAcdYr,
  ]);

  // caution tab when i trigger district api after schoolalertlist api want to call
  const [triggerCautionAlertsList] = useLazyGetSchoolAlertsListQuery();
  useEffect(() => {
    // This only warms the cache for the School Dashboard's Caution sub-tab, so
    // skip it on every other nav (Review Dashboard, Infrastructure, Academic,
    // Attendance) — it was firing there needlessly, especially now that the
    // Review grid syncs a district. The Caution tab itself fetches on demand.
    if (activeNav !== 'school-dashboard' || activeSubTab === 'caution') return;
    if (!s.loaded || selectedAcdYr == null || selectedDistrictId == null)
      return;
    const toIds = (ids: string[]) =>
      ids.map(Number).filter((n) => !Number.isNaN(n));
    const manage = toIds(fltManagement);
    const cate = toIds(fltCategory);
    const stype = toIds(fltSchoolType);
    triggerCautionAlertsList(
      {
        acd_yr: selectedAcdYr,
        district_id: selectedDistrictId,
        ...(selectedBlockId != null && { block_id: selectedBlockId }),
        ...(manage.length > 0 && { manage_id: manage }),
        ...(cate.length > 0 && { cate_id: cate }),
        ...(stype.length > 0 && { school_type_id: stype }),
        alert_type: 1,
      },
      false
    );
  }, [
    activeNav,
    activeSubTab,
    s.loaded,
    selectedAcdYr,
    selectedDistrictId,
    selectedBlockId,
    fltManagement,
    fltCategory,
    fltSchoolType,
    triggerCautionAlertsList,
  ]);

  // Global filters row (showFilters, fltManagement/fltCategory/fltSchoolType)
  // is declared earlier, above the school-abstract query that depends on it.

  // ── Filter dropdown options, driven by the commonmasters API ──

  // Log the commonmasters payload so we can see exactly which arrays/keys the
  // API returns for the filter dropdowns.
  useEffect(() => {
    if (commonMasters?.result) {
      //console.log(
      //   '[commonmasters] result keys:',
      //   Object.keys(commonMasters.result)
      // );
      //console.log('[commonmasters] result:', commonMasters.result);
    }
  }, [commonMasters]);

  // Build dropdown options from whichever array key the API uses, picking the
  // first present label field. Tolerant to backend key/field-name variations.
  const buildOptions = (
    arrayKeys: string[],
    labelKeys: string[]
  ): { label: string; value: string }[] => {
    const result = (commonMasters?.result ?? {}) as Record<string, unknown>;
    const rows =
      (arrayKeys.map((k) => result[k]).find(Array.isArray) as
        Record<string, unknown>[] | undefined) ?? [];
    return rows
      .map((row) => {
        const labelKey = labelKeys.find(
          (k) => row[k] != null && String(row[k]).trim() !== ''
        );
        const idVal = row.id ?? row.ID ?? row[labelKey ?? ''];
        return labelKey
          ? { label: String(row[labelKey]), value: String(idVal) }
          : null;
      })
      .filter((o): o is { label: string; value: string } => o != null);
  };

  // School Programme options (for Review Dashboard tab only)
  // Dummy options: All, Model School, Vetri Palli, TN SPARKS, PAL
  const programmeOptions = [
    { label: 'All', value: 'all' },
    { label: 'Model School', value: 'model' },
    { label: 'Vetri Palli', value: 'vetri' },
    { label: 'TN SPARKS', value: 'sparks' },
    { label: 'PAL', value: 'pal' },
  ];

  // Management ← `management`, filtered by the selected School Type via each
  // row's mana_cate_id (manage_cate id). Shows all when no School Type chosen.
  const managementOptions = useMemo(() => {
    const sel = new Set(fltSchoolType.map(String));
    return managementRows
      .filter((row) => sel.size === 0 || sel.has(String(row.mana_cate_id)))
      .filter(
        (row) =>
          !restrictSchoolTypes ||
          ALLOWED_SCHOOL_TYPE_IDS.has(Number(row.mana_cate_id))
      )
      .map((row) => {
        const label = String(
          row.management ?? row.management_name ?? row.name ?? ''
        );
        return label ? { label, value: String(row.id) } : null;
      })
      .filter((o): o is { label: string; value: string } => o != null);
  }, [managementRows, fltSchoolType, restrictSchoolTypes]);

  // Selecting Management(s) auto-selects the School Type(s) they belong to —
  // the reverse of the School Type → Management narrowing above, so the two
  // dropdowns stay mutually in sync either direction. Clearing Management
  // leaves School Type as-is (only an explicit School Type change resets
  // Management, matching that filter's own onChange below).
  const handleManagementFilterChange = (v: string[]) => {
    setFltManagement(v);
    if (v.length === 0) return;
    const cateIds = Array.from(
      new Set(
        v
          .map((id) => managementIdToCateId.get(String(id)))
          .filter((c): c is string => c != null)
      )
    );
    if (cateIds.length > 0) setFltSchoolType(cateIds);
  };

  // Category ← `category`.
  const categoryOptions = useMemo(
    () =>
      buildOptions(
        ['category', 'school_category', 'categories'],
        ['category_name', 'name', 'categoryname']
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [commonMasters]
  );

  // School Type ← `manage_cate` (or school_type). Restricted to Government /
  // Partially Aided / Fully Aided (ids 1, 2, 4) on Academic Scores + Attendance.
  const schoolTypeOptions = useMemo(() => {
    const all = buildOptions(
      ['manage_cate', 'school_type', 'schooltype', 'school_type_mas'],
      ['manage_name', 'category_name', 'name', 'school_type']
    );
    return restrictSchoolTypes
      ? all.filter((o) => ALLOWED_SCHOOL_TYPE_IDS.has(Number(o.value)))
      : all;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commonMasters, restrictSchoolTypes]);

  const clearHeaderFilters = () => {
    setFltProgramme(null);
    setFltManagement([]);
    setFltCategory([]);
    setFltSchoolType([]);
  };

  // The filter selection passed to every tab's drill-down summary table.
  const drillFilters = useMemo(
    () => ({
      management: fltManagement,
      category: fltCategory,
      schoolType: fltSchoolType,
    }),
    [fltManagement, fltCategory, fltSchoolType]
  );

  // Caution sub-tab badge count — the live total of flagged schools across all
  // caution checks, scoped by the current year / geography / classification.
  // Mirrors Caution.tsx's baseArgs so the badge matches the Caution page total.
  const cautionAlertArgs = useMemo(() => {
    const toIds = (ids: string[]) =>
      ids.map(Number).filter((n) => !Number.isNaN(n));
    const manage = toIds(fltManagement);
    const cate = toIds(fltCategory);
    const stype = toIds(fltSchoolType);
    return {
      acd_yr: selectedAcdYr as number,
      ...(selectedDistrictId != null && { district_id: selectedDistrictId }),
      ...(selectedBlockId != null && { block_id: selectedBlockId }),
      ...(selectedSchoolId != null && { school_id: selectedSchoolId }),
      ...(manage.length > 0 && { manage_id: manage }),
      ...(cate.length > 0 && { cate_id: cate }),
      ...(stype.length > 0 && { school_type_id: stype }),
    };
  }, [
    selectedAcdYr,
    selectedDistrictId,
    selectedBlockId,
    selectedSchoolId,
    fltManagement,
    fltCategory,
    fltSchoolType,
  ]);

  // Only call api/dashboard/schoolalerts when the user is on the Caution
  // sub-tab — not on first page load.
  const { data: cautionAlerts } = useGetSchoolAlertsQuery(cautionAlertArgs, {
    skip: selectedAcdYr == null || activeSubTab !== 'caution',
  });

  const cautionTotal = useMemo(() => {
    const result = cautionAlerts?.result;
    const toNum = (v: unknown): number => {
      const n = Number(typeof v === 'string' ? v.replace(/,/g, '').trim() : v);
      return Number.isNaN(n) ? 0 : n;
    };
    if (Array.isArray(result)) {
      return result.reduce((s, r) => s + toNum(r.count), 0);
    }
    if (result && typeof result === 'object') {
      return Object.values(result as Record<string, unknown>).reduce(
        (s: number, v) => s + toNum(v),
        0
      );
    }
    return 0;
  }, [cautionAlerts]);

  // Admin click → logout (clears session, then redirects to the external
  // TN EMIS site instead of this app's own login page).
  const handleLogout = () => {
    localStorage.clear();
    window.location.href =
      process.env.REACT_APP_API_environment == 'PRODUCTION'
        ? 'https://tnemis.tnschools.gov.in'
        : 'https://tnemis-staging.tnsed.com/';
  };

  // Report drill-down state is declared earlier (above the header dropdown
  // handlers that keep it in sync).

  // Per-school rows for the drilled district — the source for Blocks (L2,
  // aggregated by block), Schools (L3, filtered by block) and school detail.
  // Fires once a district is drilled → GET studentchildcountallyear
  // ?acd_yr=<year>&district_id=<id>. Cached per district by RTK Query.
  const { data: drillChildCount } = useGetStudentChildCountAllYearQuery(
    { acd_yr: selectedAcdYr as number, district_id: drillDistrictId as number },
    {
      skip:
        !s.loaded ||
        selectedAcdYr == null ||
        drillDistrictId == null ||
        activeNav !== 'school-dashboard',
    }
  );

  // Diagnostic: confirm the drill query fires and what it returns.
  useEffect(() => {
    //console.log(
    //   `[drill] district=${drillDistrict} (id=${drillDistrictId}) block=${drillBlock} rows=${
    //     drillChildCount?.result?.length ?? 0
    //   }`
    // );
    if (drillChildCount?.result?.length) {
      //console.log('[drill] sample row:', drillChildCount.result[0]);
    }
  }, [drillChildCount, drillDistrict, drillDistrictId, drillBlock]);

  // Apply the active Management / Category / School-Type filter to per-school
  // rows (studentchildcountallyear carries manage_id / catty_id /
  // school_type_id). Returns the rows unchanged when no filter is active.
  const applyClassFilter = (rows: Record<string, unknown>[]) => {
    const mgmt = fltManagement.map(Number);
    const cat = fltCategory.map(Number);
    const stype = fltSchoolType.map(Number);
    if (mgmt.length === 0 && cat.length === 0 && stype.length === 0) {
      return rows;
    }
    return rows.filter((r) => {
      const okMgmt = mgmt.length === 0 || mgmt.includes(Number(r.manage_id));
      const okCat =
        cat.length === 0 || cat.includes(Number(r.cate_id ?? r.cate_id));
      const okType =
        stype.length === 0 || stype.includes(Number(r.school_type_id));
      return okMgmt && okCat && okType;
    });
  };

  const reportView = useMemo(() => {
    const childRows = applyClassFilter(
      (drillChildCount?.result ?? []) as Record<string, unknown>[]
    );

    // Level 3: Schools of a block — per-school rows filtered by block_id
    // (reliable), falling back to block_name when the id isn't captured.
    if (drillDistrict && drillBlock) {
      const rows = childRows
        .filter((r) =>
          drillBlockId != null
            ? Number(r.block_id) === drillBlockId
            : String(r.block_name ?? '') === drillBlock
        )
        .map((r) => {
          const boys = num(r.total_b);
          const girls = num(r.total_g);
          const schoolName = String(r.school_name ?? '');
          return {
            // Always "School Name (udise)", falling back to "(0)" when the row
            // carries no UDISE, so the code is shown everywhere a school is.
            name: withUdise(schoolName, String(r.udise_code ?? '')),
            school_id: Number(r.school_id ?? 0),
            boys,
            girls,
            students: boys + girls, // students = boys + girls
            teachers: num(r.teach_tot),
          };
        });
      return {
        title: `Schools — ${drillBlock}, ${drillDistrict}`,
        hint: drillHint('school', true),
        searchPlaceholder: drillSearchPlaceholder('school'),
        exportFileName: `schools-${drillBlock}`,
        data: rows as unknown as Record<string, unknown>[],
        columns: [
          { key: 'name', label: 'Name', type: 'link' },
          { key: 'boys', label: 'Boys', type: 'number' },
          { key: 'girls', label: 'Girls', type: 'number' },
          { key: 'students', label: 'Students', type: 'number', bold: true },
          { key: 'teachers', label: 'Teachers', type: 'number' },
        ] as ReportColumn[],
        level: 3 as const,
      };
    }
    // Level 2: Blocks of a district — aggregate per-school rows by block_id.
    if (drillDistrict) {
      const byBlock = new Map<
        number,
        {
          name: string;
          block_id: number;
          schools: number;
          boys: number;
          girls: number;
          students: number;
          teachers: number;
        }
      >();
      childRows.forEach((r) => {
        const blockId = Number(r.block_id ?? 0);
        const name = String(r.block_name ?? '');
        if (!blockId && !name) return;
        const acc = byBlock.get(blockId) ?? {
          name,
          block_id: blockId,
          schools: 0,
          boys: 0,
          girls: 0,
          students: 0,
          teachers: 0,
        };
        const boys = num(r.total_b);
        const girls = num(r.total_g);
        acc.schools += 1;
        acc.boys += boys;
        acc.girls += girls;
        acc.students += boys + girls; // students = boys + girls
        acc.teachers += num(r.teach_tot);
        byBlock.set(blockId, acc);
      });
      const rows = Array.from(byBlock.values()).sort(
        (a, b) => b.students - a.students
      );
      return {
        title: `Blocks — ${drillDistrict}`,
        hint: drillHint('block'),
        searchPlaceholder: drillSearchPlaceholder('block'),
        exportFileName: `blocks-${drillDistrict}`,
        data: rows as unknown as Record<string, unknown>[],
        columns: [
          { key: 'name', label: 'Name', type: 'link' },
          { key: 'schools', label: 'Schools', type: 'number' },
          { key: 'boys', label: 'Boys', type: 'number' },
          { key: 'girls', label: 'Girls', type: 'number' },
          { key: 'students', label: 'Students', type: 'number', bold: true },
          { key: 'teachers', label: 'Teachers', type: 'number' },
        ] as ReportColumn[],
        level: 2 as const,
      };
    }
    // Level 1: Districts — all districts. Filter active → scoped abstract
    // districts; else the dashboardsummary per-district rows.
    const abstractDistricts = schoolAbstract?.result?.districts ?? [];
    const level1Rows =
      hasAbstractFilter && abstractDistricts.length > 0
        ? abstractDistricts.map((d) => ({
            name: String(d.district_name ?? ''),
            district_id: Number(d.district_id ?? 0),
            schools: num(d.schools),
            boys: num(d.total_boys),
            girls: num(d.total_girls),
            students: num(d.total_students),
            teachers: num(d.teachers),
          }))
        : parseSchoolDashboardRows(
            dashboardSummary?.result?.['School Dashboard']?.data
          )
            .filter(
              (r) => Number((r as Record<string, unknown>).district_id) > 0
            )
            .map((r) => {
              const row = r as Record<string, unknown>;
              return {
                name: String(row.district_name ?? ''),
                district_id: Number(row.district_id ?? 0),
                schools: num(row.total_schools),
                boys: num(row.total_boys),
                girls: num(row.total_girls),
                students: num(row.total_students),
                teachers: num(row.total_teachers),
              };
            });
    const level1Data = level1Rows.sort(
      (a, b) => b.students - a.students
    ) as unknown as Record<string, unknown>[];

    return {
      title: 'Districts — Tamil Nadu',
      hint: drillHint('district'),
      searchPlaceholder: drillSearchPlaceholder('district'),
      exportFileName: 'districts-tamil-nadu',
      data: level1Data,
      columns: [
        { key: 'name', label: 'Name', type: 'link' },
        { key: 'schools', label: 'Schools', type: 'number' },
        { key: 'boys', label: 'Boys', type: 'number' },
        { key: 'girls', label: 'Girls', type: 'number' },
        { key: 'students', label: 'Students', type: 'number', bold: true },
        { key: 'teachers', label: 'Teachers', type: 'number' },
      ] as ReportColumn[],
      level: 1 as const,
    };
  }, [
    drillDistrict,
    drillBlock,
    drillBlockId,
    dashboardSummary,
    schoolAbstract,
    hasAbstractFilter,
    drillChildCount,
    fltManagement,
    fltCategory,
    fltSchoolType,
  ]);

  // Drill-down report pagination. `reportFirst` is the controlled page offset
  // of the report grid; the ref remembers the Districts-level page so Back can
  // restore it (drilling into a district otherwise resets the grid to page 1).
  const [reportFirst, setReportFirst] = useState(0);
  const savedDistrictFirstRef = React.useRef(0);

  const handleReportRowClick = (row: Record<string, unknown>) => {
    const name = String(row.name ?? '');
    if (reportView.level === 1) {
      // Remember which Districts page we drilled from, so Back can restore it.
      savedDistrictFirstRef.current = reportFirst;
      // Capture the district id for the drill API. Prefer the master id
      // (from alldistrictlist, matched by name) which the API expects;
      // fall back to the row's own district_id.
      const idFromMaster = districtNameToId.get(name);
      const idFromRow = Number(row.district_id ?? 0) || null;
      setDrillDistrictId(idFromMaster ?? idFromRow);
      setDrillDistrict(name);
      // Inner grid (Blocks) starts at page 1.
      setReportFirst(0);
      // Keep the header District dropdown in sync.
      setHeaderDistrict(name);
      setHeaderBlock(null);
      setHeaderSchool(null);
    } else if (reportView.level === 2) {
      // Capture the block id so the drill can return its schools.
      setDrillBlockId(Number(row.block_id ?? 0) || null);
      setDrillBlock(name);
      // Inner grid (Schools) starts at page 1.
      setReportFirst(0);
      // Keep the header Block dropdown in sync.
      setHeaderBlock(name);
      setHeaderSchool(null);
    } else if (reportView.level === 3) {
      // Level 3 (schools) is the deepest table level: clicking a school row
      // drills into the single-school detail view. Build selectedSchool from
      // the row (name + school_id); schoolDetail matches the API child-count
      // row by school_id, then falls back to the normalized name.
      if (!name) return;
      // The grid row's `name` is "School Name (UDISE)" (built in reportView),
      // but the header School dropdown's option values are the bare
      // school_name. Sync ONLY the dropdown with the plain name — stripping
      // the trailing " (udise)" — so the selected row shows up in the dropdown
      // instead of leaving it blank. `selectedSchool` keeps the original row
      // name so the school-detail view (which drives SchoolDetailView) builds
      // exactly as it did before this fix.
      const schoolName = name.replace(/\s*\(\d[\d/]*\)\s*$/, '').trim();
      setHeaderSchool(schoolName);
      setSelectedSchool({
        name,
        school_id: Number(row.school_id ?? 0),
        udise_code: String(row.udise_code ?? ''),
      } as unknown as SchoolRow);
    }
  };

  const reportGoBack = () => {
    // Peel back one level at a time: school detail → schools → blocks → root.
    // Keep the header dropdowns in sync as we go back up.
    if (selectedSchool) {
      setSelectedSchool(null);
      setHeaderSchool(null);
    } else if (drillBlock) {
      if (!blockLocked) {
        setDrillBlock(null);
        setDrillBlockId(null);
        setHeaderBlock(null);
      }
    } else if (drillDistrict) {
      if (!districtLocked) {
        setDrillDistrict(null);
        setDrillDistrictId(null);
        setHeaderDistrict(null);
        // Restore the Districts-grid page the user was on before drilling in.
        setReportFirst(savedDistrictFirstRef.current);
      }
    }
  };

  // (The District-wise chart now drills via handleReportRowClick — the same
  // handler the grid rows use — so the chart and grid advance together through
  // district → block → school. The old handleDistrictDrill is no longer needed.)

  // Real per-school rows for the drilled district (full identity fields:
  // Full detail for the currently-selected school (charts + panel). Starts
  // from the seeded mock, then overrides the identity fields with the real
  // API row (matched by school_id) from the drilled district's child-count.
  const schoolDetail = useMemo(() => {
    if (!selectedSchool || !drillDistrict || !drillBlock) return null;
    const base = getSchoolDetail(
      drillDistrict,
      drillBlock,
      selectedSchool.name,
      selectedSchool
    );

    // Find the real API row for the selected school. Match by school_id first;
    // fall back to UDISE code, then to the (normalized) school name — the
    // header dropdown and the drill table can carry different id spaces, so a
    // single id match isn't always enough.
    const sel = selectedSchool as unknown as Record<string, unknown>;
    const selId = Number(sel.school_id ?? 0);
    const selUdise = String(sel.udise_code ?? '').trim();
    const selName = String(selectedSchool.name ?? '')
      .trim()
      .toLowerCase();
    const rows = (drillChildCount?.result ?? []).filter((r) =>
      // Scope to the selected block when its id is known (reliable), else by
      // block name — so the match can't collide with a same-named school in
      // another block.
      drillBlockId != null
        ? Number(r.block_id) === drillBlockId
        : String(r.block_name ?? '') === drillBlock
    );
    const row =
      (selId > 0 && rows.find((r) => Number(r.school_id) === selId)) ||
      (selUdise &&
        rows.find((r) => String(r.udise_code ?? '').trim() === selUdise)) ||
      rows.find(
        (r) =>
          String(r.school_name ?? '')
            .trim()
            .toLowerCase() === selName
      );
    if (!row) return base;

    const boys = num(row.total_b);
    const girls = num(row.total_g);
    const trans = num(row.total_t);
    const students = boys + girls + trans;
    const teachers = num(row.teach_tot);
    const nonTeaching = num(row.nonteach_tot);
    // const nonTeaching = num(row.nonteach_tot) + num(row.admin_staff_tot);
    const totalStaff = num(row.totstaff) || teachers + nonTeaching;

    // Rebuild the Gender Split donut from the REAL API counts (total_b/total_g/
    // total_t). Previously the single-school view inherited the seeded mock
    // genderSplit from `base`, so a boys-only school could show girls > boys.
    const genderSplit: ChartSlice[] = [
      {
        label: 'Boys',
        value: asPct(boys, students),
        color: '#f0803c',
        count: boys,
      },
      {
        label: 'Girls',
        value: asPct(girls, students),
        color: '#26a69a',
        count: girls,
      },
      {
        label: 'Transgender',
        value: asPct(trans, students),
        color: '#7e57c2',
        count: trans,
      },
    ];

    // Rebuild the Social Category donut from the real row (bc/mbc/st/sc/oc/dnc).
    const soc = [
      { label: 'OC', value: num(row.oc), color: '#f2c14e' },
      { label: 'BC', value: num(row.bc), color: '#26a69a' },
      { label: 'MBC', value: num(row.mbc), color: '#8bc34a' },
      { label: 'DNC', value: num(row.dnc), color: '#c62828' },
      { label: 'SC', value: num(row.sc), color: '#f0803c' },
      { label: 'ST', value: num(row.st), color: '#2f5fb0' },
    ];
    const socTotal = soc.reduce((a, x) => a + x.value, 0);
    const socialCategory: ChartSlice[] = soc.map((x) => ({
      label: x.label,
      value: asPct(x.value, socTotal),
      color: x.color,
      count: x.value,
    }));

    // Rebuild Enrolment by Grade from the real per-grade boys/girls columns.
    const gradeKeys = [
      ['Pre-KG', 'prkg'],
      ['LKG', 'lkg'],
      ['UKG', 'ukg'],
      ['I', 'c1'],
      ['II', 'c2'],
      ['III', 'c3'],
      ['IV', 'c4'],
      ['V', 'c5'],
      ['VI', 'c6'],
      ['VII', 'c7'],
      ['VIII', 'c8'],
      ['IX', 'c9'],
      ['X', 'c10'],
      ['XI', 'c11'],
      ['XII', 'c12'],
    ] as const;
    const enrolmentByGrade = {
      grades: gradeKeys.map(([label]) => label),
      boys: gradeKeys.map(([, k]) => num(row[`${k}_b`])),
      girls: gradeKeys.map(([, k]) => num(row[`${k}_g`])),
      trans: gradeKeys.map(([, k]) => num(row[`${k}_t`])),
    };

    // Recompute the secondary metrics from the REAL counts. getSchoolDetail
    // derived these from the seeded mock (selectedSchool carries no counts),
    // so the four cards (GPI / PTR / Girls Share / Avg Students) showed mock
    // values that didn't match the school's real boys/girls/students/teachers.
    const gpi = boys > 0 ? girls / boys : 0;
    const ptr = teachers > 0 ? students / teachers : 0;
    const girlsShare = students > 0 ? (girls / students) * 100 : 0;
    const metrics = {
      ...base.metrics,
      gpi: gpi.toFixed(2),
      ptr: `${ptr.toFixed(1)} : 1`,
      girlsShare: `${girlsShare.toFixed(1)}%`,
      // One school → avg students per school is just its own student count.
      avgStudents: String(students),
    };

    return {
      ...base,
      name: String(row.school_name ?? ''),
      udiseCode: String(row.udise_code ?? ''),
      management: String(row.management ?? ''),
      category: String(row.category ?? ''),
      schoolType: String(row.school_type ?? ''),
      level: String(row.cate_type ?? ''),
      boys,
      girls,
      students,
      teachers,
      totalStaff,
      nonTeaching,
      // Real-data donuts/series (override the seeded mock ones from `base`).
      genderSplit,
      socialCategory,
      enrolmentByGrade,
      // Real-data secondary metrics (override the seeded mock ones).
      metrics,
      kpi: { ...base.kpi, schools: 1, boys, girls, students, teachers },
    };
  }, [
    selectedSchool,
    drillDistrict,
    drillBlock,
    drillBlockId,
    drillChildCount,
  ]);
  // District / block overview (KPI tiles + metrics + charts) shown above the
  // next-level table while drilling. Block level takes precedence over the
  // district; a selected school suppresses this in favour of the school view.
  const entityDetail = useMemo(() => {
    if (selectedSchool) return null;
    // Scope the drilled district's rows by the active classification filter so
    // the overview (e.g. ARIYALUR + Army Public School) reflects the filter.
    const childRows = applyClassFilter(
      (drillChildCount?.result ?? []) as Record<string, unknown>[]
    );
    const aggregate = (rows: Record<string, unknown>[]) =>
      rows.reduce(
        (
          a: {
            schools: number;
            boys: number;
            girls: number;
            trans: number;
            students: number;
            teachers: number;
          },
          r
        ) => {
          const boys = num(r.total_b);
          const girls = num(r.total_g);
          const trans = num(r.total_t);
          a.schools += 1;
          a.boys += boys;
          a.girls += girls;
          a.trans += trans;
          a.students += boys + girls + trans; // students = boys + girls + trans
          a.teachers += num(r.teach_tot);
          return a;
        },
        { schools: 0, boys: 0, girls: 0, trans: 0, students: 0, teachers: 0 }
      );

    // Block level → aggregate the drilled district's rows for this block
    // (match by block_id when captured, else by name).
    if (drillDistrict && drillBlock) {
      const agg = aggregate(
        childRows.filter((r) =>
          drillBlockId != null
            ? Number(r.block_id) === drillBlockId
            : String(r.block_name ?? '') === drillBlock
        )
      );
      const base: EntityBase = { name: drillBlock, ...agg };
      return getEntityDetail(
        `${drillDistrict}:${drillBlock}`,
        base,
        'Schools Level'
      );
    }
    // District level → aggregate all the drilled district's rows.
    if (drillDistrict) {
      const agg = aggregate(childRows);
      const base: EntityBase = { name: drillDistrict, ...agg };
      return getEntityDetail(drillDistrict, base, 'Blocks Level');
    }
    return null;
  }, [
    drillDistrict,
    drillBlock,
    drillBlockId,
    selectedSchool,
    drillChildCount,
    fltManagement,
    fltCategory,
    fltSchoolType,
  ]);

  // KPI / metrics for the overview tiles: scope to the drilled entity
  // (school → block/district) when selected, else the state-level summary.
  const dispKpi = schoolDetail?.kpi ?? entityDetail?.kpi ?? schoolDash?.kpi;
  const dispMetrics =
    schoolDetail?.metrics ?? entityDetail?.metrics ?? schoolDash?.metrics;
  // Ref to the dashboard content container, captured for the "Export PDF"
  // (full visual page) action.
  const contentRef = React.useRef<HTMLElement>(null);
  const [exportingPdf, setExportingPdf] = useState(false);
  // Global full-screen loader (same one Login uses), shown while a PDF export
  // runs so the user gets feedback during the (potentially slow) capture.
  const { showLoader, hideLoader } = useUI();

  // Capture the currently-visible dashboard content to a PDF (KPI tiles, map,
  // charts, table — whatever is on screen for the active tab/scope).
  const handleExportPdf = async () => {
    const el = contentRef.current;
    if (!el || exportingPdf) return;
    setExportingPdf(true);
    showLoader();
    try {
      const scope =
        headerSchool ?? headerBlock ?? headerDistrict ?? 'tamil-nadu';
      // Build slug: exclude activeSubTab for Review Dashboard (it has no subtabs)
      const isReviewTab = activeNav === 'review-dashboard';
      const slugParts = [
        'dashboard',
        activeNav,
        !isReviewTab && activeSubTab ? activeSubTab : null,
        scope,
      ]
        .filter(Boolean)
        .join('-');
      const slug = slugParts
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
      // Same local-time stamp as the grid and chart downloads.
      const fileName = `${slug}-${exportTimestamp()}`;
      await exportElementToPdf({ element: el, fileName });
    } catch (err) {
      console.error('[Export PDF] failed:', err);
    } finally {
      setExportingPdf(false);
      // Always hide the loader, even if the export failed.
      hideLoader();
    }
  };

  // Open Map panel (Tamil Nadu district choropleth). Open by default; the
  // effect below keeps it in sync with the school selection (open at
  // district/block level, auto-closed when a school is selected).
  const [showMap, setShowMap] = useState(true);

  // Map visibility follows the school selection: close the map when a school is
  // selected (so the full-width School Details page shows, no map squeezed
  // beside it), and re-open it when the school is cleared — e.g. going Back to
  // the block level — so the button reads "Close Map" again there.
  useEffect(() => {
    setShowMap(!selectedSchool);
  }, [selectedSchool]);

  const [mapMetric, setMapMetric] = useState('total-students');
  const mapMetrics = [
    { label: 'Total Students', value: 'total-students' },
    { label: 'Total Schools', value: 'total-schools' },
    { label: 'Boys Enrolment', value: 'boys' },
    { label: 'Girls Enrolment', value: 'girls' },
    { label: 'Teachers', value: 'teachers' },
  ];
  // district_id → value for the selected metric, from the per-district API
  // rows. Keyed by district_id (which matches the SVG polygon ids) so the
  // choropleth is name-spelling-proof. Rows with district_id === 0 are the
  // STATE total and are skipped (they are not a district polygon). Unmatched
  // districts get 0 (no dummy values).
  const mapData = useMemo(() => {
    const out: Record<string, number> = {};
    const abstractDistricts = schoolAbstract?.result?.districts ?? [];
    if (hasAbstractFilter && abstractDistricts.length > 0) {
      const abstractKeyByMetric: Record<string, string> = {
        'total-students': 'total_students',
        'total-schools': 'schools',
        boys: 'total_boys',
        girls: 'total_girls',
        teachers: 'teachers',
      };
      const key = abstractKeyByMetric[mapMetric] ?? 'total_students';
      abstractDistricts.forEach((d) => {
        const row = d as Record<string, unknown>;
        const id = Number(row.district_id ?? 0);
        if (id > 0) out[String(id)] = num(row[key]);
      });
    } else {
      const summaryKeyByMetric: Record<string, string> = {
        'total-students': 'total_students',
        'total-schools': 'total_schools',
        boys: 'total_boys',
        girls: 'total_girls',
        teachers: 'total_teachers',
      };
      const key = summaryKeyByMetric[mapMetric] ?? 'total_students';
      parseSchoolDashboardRows(
        dashboardSummary?.result?.['School Dashboard']?.data
      )
        .filter((r) => Number((r as Record<string, unknown>).district_id) > 0)
        .forEach((r) => {
          const row = r as Record<string, unknown>;
          const id = Number(row.district_id ?? 0);
          if (id > 0) out[String(id)] = num(row[key]);
        });
    }
    return out;
  }, [mapMetric, dashboardSummary, schoolAbstract, hasAbstractFilter]);

  // Stats for the current map selection, pulled from the SAME API memos the
  // rest of the dashboard uses — so the numbers always match the tables:
  //  • school selected → the single school's row (schoolDetail)
  //  • block selected  → that block's aggregate (entityDetail / aggregate)
  //  • district only    → that district's aggregate (entityDetail)
  // Returns null (panel hidden) when nothing is selected or data isn't ready.
  const mapSelectionStats = useMemo(() => {
    if (selectedSchool && schoolDetail) {
      return {
        scope: headerSchool ?? selectedSchool.name ?? 'School',
        level: 'School',
        schools: 1,
        boys: num(schoolDetail.boys),
        girls: num(schoolDetail.girls),
        teachers: num(schoolDetail.teachers),
      };
    }
    if (entityDetail) {
      const kpi = entityDetail.kpi;
      return {
        scope: entityDetail.name || headerBlock || headerDistrict || '',
        level: drillBlock ? 'Block' : 'District',
        schools: num(kpi?.schools),
        boys: num(kpi?.boys),
        girls: num(kpi?.girls),
        teachers: num(kpi?.teachers),
      };
    }
    return null;
  }, [
    selectedSchool,
    schoolDetail,
    entityDetail,
    headerSchool,
    headerBlock,
    headerDistrict,
    drillBlock,
  ]);

  // When a district / block (or a single school) is drilled, build the
  // Management-Wise donut from the per-school drill rows — grouping students
  // (total_b + total_g) by each row's manage_id, labelled via the commonmasters
  // management names. This scopes the chart to the selection instead of the
  // state-level dashboardsummary breakdown. Returns null when no drill scope.
  const districtManagementWise = useMemo<ChartSlice[] | null>(() => {
    if (drillDistrictId == null) return null;
    let rows = applyClassFilter(
      (drillChildCount?.result ?? []) as Record<string, unknown>[]
    );
    // When a single school is selected, scope to that school's row(s) only, so
    // the donut reflects just that school's management.
    if (selectedSchool) {
      const sel = selectedSchool as unknown as Record<string, unknown>;
      const selId = Number(sel.school_id ?? 0);
      const selName = String(selectedSchool.name ?? '')
        .trim()
        .toLowerCase();
      rows = rows.filter((r) =>
        selId > 0
          ? Number(r.school_id) === selId
          : String(r.school_name ?? '')
              .trim()
              .toLowerCase() === selName
      );
    } else if (drillBlock) {
      // Block selected → scope to that block's schools.
      rows = rows.filter((r) =>
        drillBlockId != null
          ? Number(r.block_id) === drillBlockId
          : String(r.block_name ?? '') === drillBlock
      );
    }
    if (rows.length === 0) return null;

    // manage_id → management name, from the commonmasters payload.
    const masterRows = (
      Array.isArray(
        (commonMasters?.result as Record<string, unknown> | undefined)
          ?.management
      )
        ? (commonMasters!.result as Record<string, unknown>).management
        : []
    ) as Record<string, unknown>[];
    const idToName = new Map<number, string>();
    masterRows.forEach((m) => {
      const id = Number(m.id);
      const name = String(m.management ?? m.management_name ?? m.name ?? '');
      if (id && name) idToName.set(id, name);
    });

    // Reuse the state-level palette for known buckets; cycle a fallback palette
    // for any other management names the API returns.
    const knownColors: Record<string, string> = {
      Government: '#f0803c',
      Private: '#f2c14e',
      Aided: '#26a69a',
      'Partial Aided': '#8bc34a',
      'Central Govt': '#c62828',
      Others: '#7e57c2',
    };
    // Collapse the granular commonmasters management names (e.g. "Un-aided

    const bucketOf = (nm: string): string => {
      const n = nm.toLowerCase();
      if (n.includes('central')) return 'Central Govt';
      // "un-aided" contains "aided", so check private / un-aided FIRST.
      if (
        n.includes('un-aided') ||
        n.includes('unaided') ||
        n.includes('private')
      )
        return 'Private';
      if (n.includes('partial')) return 'Partial Aided';
      if (n.includes('aided')) return 'Aided';
      if (
        n.includes('govern') ||
        n.includes('govt') ||
        n.includes('department') ||
        n.includes('local body') ||
        n.includes('panchayat') ||
        n.includes('municipal') ||
        n.includes('corporation') ||
        n.includes('adi dravidar') ||
        n.includes('tribal') ||
        n.includes('forest')
      )
        return 'Government';
      return 'Others';
    };

    const byMgmt = new Map<string, number>();
    rows.forEach((r) => {
      const id = Number(r.manage_id);
      const name =
        idToName.get(id) || String(r.management ?? '').trim() || 'Others';
      const students = num(r.total_b) + num(r.total_g);
      const bucket = bucketOf(name);
      byMgmt.set(bucket, (byMgmt.get(bucket) ?? 0) + students);
    });

    const total = Array.from(byMgmt.values()).reduce((a, v) => a + v, 0);
    if (total === 0) return null;

    // Fixed, friendly order so the slices read consistently.
    const order = [
      'Government',
      'Private',
      'Aided',
      'Partial Aided',
      'Central Govt',
      'Others',
    ];
    return order
      .filter((label) => (byMgmt.get(label) ?? 0) > 0)
      .map((label) => ({
        label,
        value: asPct(byMgmt.get(label) as number, total),
        color: knownColors[label] ?? '#7e57c2',
        count: byMgmt.get(label) as number,
      }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    drillDistrictId,
    drillChildCount,
    commonMasters,
    selectedSchool,
    drillBlock,
    drillBlockId,
    fltManagement,
    fltCategory,
    fltSchoolType,
  ]);

  // School-Type breakdown scoped to the drilled district/block/school, grouped
  // by each child-count row's `school_type_id` (the manage_cate dimension),
  // mapped to names via the commonmasters manage_cate master. Mirrors
  // districtManagementWise so the "School Type" donut re-scopes with the
  // district / block / school selection AND the Management / Category /
  // School-Type filters (applyClassFilter). null (→ falls back to the
  // state-level schoolDash.schoolType) when nothing is drilled.
  const districtSchoolType = useMemo<ChartSlice[] | null>(() => {
    if (drillDistrictId == null) return null;
    let rows = applyClassFilter(
      (drillChildCount?.result ?? []) as Record<string, unknown>[]
    );
    // Single school selected → scope to its row(s).
    if (selectedSchool) {
      const sel = selectedSchool as unknown as Record<string, unknown>;
      const selId = Number(sel.school_id ?? 0);
      const selName = String(selectedSchool.name ?? '')
        .trim()
        .toLowerCase();
      rows = rows.filter((r) =>
        selId > 0
          ? Number(r.school_id) === selId
          : String(r.school_name ?? '')
              .trim()
              .toLowerCase() === selName
      );
    } else if (drillBlock) {
      // Block selected → scope to its schools.
      rows = rows.filter((r) =>
        drillBlockId != null
          ? Number(r.block_id) === drillBlockId
          : String(r.block_name ?? '') === drillBlock
      );
    }
    if (rows.length === 0) return null;

    // school_type_id → name, from the commonmasters manage_cate master (the
    // same master the School Type filter dropdown is built from).
    const masterResult = (commonMasters?.result ?? {}) as Record<
      string,
      unknown
    >;
    const masterRows = ([
      'manage_cate',
      'school_type',
      'schooltype',
      'school_type_mas',
    ]
      .map((k) => masterResult[k])
      .find(Array.isArray) ?? []) as Record<string, unknown>[];
    const idToName = new Map<number, string>();
    masterRows.forEach((m) => {
      const id = Number(m.id ?? m.ID);
      const name = String(
        m.manage_name ?? m.category_name ?? m.name ?? m.school_type ?? ''
      ).trim();
      if (id && name) idToName.set(id, name);
    });

    // Student total per school_type_id.
    const byType = new Map<string, number>();
    rows.forEach((r) => {
      const id = Number(r.school_type_id ?? 0);
      const name =
        idToName.get(id) || String(r.school_type ?? '').trim() || 'Others';
      const students = num(r.total_b) + num(r.total_g);
      byType.set(name, (byType.get(name) ?? 0) + students);
    });

    const total = Array.from(byType.values()).reduce((a, v) => a + v, 0);
    if (total === 0) return null;

    // Stable palette cycled across whatever school-type names the data has.
    const palette = [
      '#f2c14e',
      '#f0803c',
      '#26a69a',
      '#8bc34a',
      '#c62828',
      '#7e57c2',
      '#2f5fb0',
      '#ec407a',
    ];
    return Array.from(byType.entries())
      .filter(([, v]) => v > 0)
      .sort((a, b) => b[1] - a[1])
      .map(([label, value], i) => ({
        label,
        value: asPct(value, total),
        color: palette[i % palette.length],
        count: value,
      }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    drillDistrictId,
    drillChildCount,
    commonMasters,
    selectedSchool,
    drillBlock,
    drillBlockId,
    fltManagement,
    fltCategory,
    fltSchoolType,
  ]);

  // Level-of-Education breakdown scoped to the drilled district/block/school,
  // aggregated from the per-grade columns on the child-count rows (prkg/lkg/
  // ukg + c1…c12, each _b/_g/_t). Mirrors districtManagementWise so the
  // "Level of Education" donut re-scopes when a district is selected instead of
  // staying on the static state-level mock. null (→ mock) when no drill scope.
  const districtLevelOfEducation = useMemo<ChartSlice[] | null>(() => {
    if (drillDistrictId == null) return null;
    let rows = applyClassFilter(
      (drillChildCount?.result ?? []) as Record<string, unknown>[]
    );
    if (selectedSchool) {
      const sel = selectedSchool as unknown as Record<string, unknown>;
      const selId = Number(sel.school_id ?? 0);
      const selName = String(selectedSchool.name ?? '')
        .trim()
        .toLowerCase();
      rows = rows.filter((r) =>
        selId > 0
          ? Number(r.school_id) === selId
          : String(r.school_name ?? '')
              .trim()
              .toLowerCase() === selName
      );
    } else if (drillBlock) {
      rows = rows.filter((r) =>
        drillBlockId != null
          ? Number(r.block_id) === drillBlockId
          : String(r.block_name ?? '') === drillBlock
      );
    }
    if (rows.length === 0) return null;

    // Sum students (b+g+t) across the given grade keys over all scoped rows.
    const sumGrades = (keys: string[]) =>
      rows.reduce(
        (acc, r) =>
          acc +
          keys.reduce(
            (s, k) =>
              s + num(r[`${k}_b`]) + num(r[`${k}_g`]) + num(r[`${k}_t`]),
            0
          ),
        0
      );

    const buckets = [
      {
        label: 'Primary',
        count: sumGrades(['prkg', 'lkg', 'ukg', 'c1', 'c2', 'c3', 'c4', 'c5']),
        color: '#f4c430',
      },
      {
        label: 'Upper Primary',
        count: sumGrades(['c6', 'c7', 'c8']),
        color: '#f0803c',
      },
      {
        label: 'Secondary',
        count: sumGrades(['c9', 'c10']),
        color: '#8bc34a',
      },
      {
        label: 'Higher Secondary',
        count: sumGrades(['c11', 'c12']),
        color: '#26a69a',
      },
    ];
    const total = buckets.reduce((a, b) => a + b.count, 0);
    if (total === 0) return null;
    return buckets
      .filter((b) => b.count > 0)
      .map((b) => ({
        label: b.label,
        value: asPct(b.count, total),
        color: b.color,
        count: b.count,
      }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    drillDistrictId,
    drillChildCount,
    selectedSchool,
    drillBlock,
    drillBlockId,
    fltManagement,
    fltCategory,
    fltSchoolType,
  ]);

  // UDISE of the currently-selected school, for display (the dropdown value /
  // headerSchool stays the bare name). Prefer the drilled school's row, else
  // the schoollist match.
  const headerSchoolUdise = useMemo(() => {
    const fromSelected = String(
      (selectedSchool as unknown as Record<string, unknown>)?.udise_code ?? ''
    ).trim();
    if (fromSelected) return fromSelected;
    if (!headerSchool) return '';
    const rows =
      schoolList?.result?.school ??
      schoolList?.result?.schools ??
      schoolList?.result?.schoollist ??
      [];
    const match = rows.find(
      (sc) => String(sc.school_name ?? '') === headerSchool
    );
    return String(match?.udise_code ?? '').trim();
  }, [selectedSchool, headerSchool, schoolList]);

  // Scope label for card / tile titles — the deepest active selection, else
  // "Tamil Nadu". `drillDistrict` is included because the report table can be
  // drilled into a district without the header dropdown being set. When the
  // scope is a school, append its UDISE for display.
  const managementScopeLabel = scopeLabel({
    district: headerDistrict ?? drillDistrict,
    block: headerBlock,
    school: headerSchool ? withUdise(headerSchool, headerSchoolUdise) : null,
  });

  // The level the rows BELOW the current scope sit at, for the "· … Level"
  // sub-label on the scope tile: nothing selected → districts, a district →
  // its blocks, a block → its schools, a school → itself.
  const scopeBreakdownLabel = headerSchool
    ? 'School Level'
    : headerBlock
      ? 'Schools Level'
      : (headerDistrict ?? drillDistrict)
        ? 'Blocks Level'
        : 'Districts Level';

  // Management-Wise slices: when the "Management-Wise" view is active AND a
  // district/block/school is drilled, use the scoped breakdown; otherwise the
  // state-level dataset for the selected view.
  const managementSlices =
    // Level of Education: use the district-scoped breakdown when a district is
    // drilled, so the donut changes with the selected district (falls back to
    // the state-level mock when nothing is drilled).
    managementView === 'level' && districtLevelOfEducation
      ? districtLevelOfEducation
      : // School Type: use the district-scoped, filter-aware breakdown (grouped
        // by school_type_id) when a district is drilled, so it re-scopes with
        // the selection + filters exactly like Level of Education. Falls back to
        // the state-level schoolDash.schoolType when nothing is drilled.
        managementView === 'schoolType' && districtSchoolType
        ? districtSchoolType
        : managementView === 'management' && districtManagementWise
          ? districtManagementWise
          : managementView === 'management' &&
              !hasAbstractFilter &&
              summaryManagementWise
            ? // First render / no filter → the dashboardsummary state-total
              // row's management counts (total_govt_students,
              // total_private_students, total_aided_students,
              // total_partial_aided_students, total_central_students).
              summaryManagementWise
            : managementDatasets[managementView];

  // ── Memoized chart cards ──────────────────────────────
  // Each depends only on its OWN state, so changing one dropdown never
  // re-renders/re-animates the others.
  const managementChart = useMemo(
    () => (
      <AppChart
        type="doughnut"
        title={`${
          managementViews.find((v) => v.value === managementView)?.label ??
          'Management-Wise'
        } — ${managementScopeLabel}`}
        icon="fa-solid fa-chart-pie"
        iconColor="#2f5fb0"
        slices={managementSlices}
        height={320}
        headerRight={
          <Dropdown
            value={managementView}
            options={managementViews}
            onChange={(e) => setManagementView(e.value)}
            className="dashboard-mini-dropdown"
            panelClassName="dashboard-mini-dropdown-panel"
          />
        }
      />
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [managementView, schoolDash, managementSlices, managementScopeLabel]
  );

  // When a district (or block) is drilled, build the Gender Split from the
  // per-school drill rows (studentchildcountallyear), which carry total_b /
  // total_g / total_t separately. This shows Transgender as its OWN slice for
  // the selected district instead of folding it into Girls — the state-level
  // dashboardsummary payload doesn't expose transgender.
  const districtGenderSplit = useMemo<ChartSlice[] | null>(() => {
    if (drillDistrictId == null) return null;
    let rows = applyClassFilter(
      (drillChildCount?.result ?? []) as Record<string, unknown>[]
    );
    // Scope to the selected school / block (same as the management, social and
    // enrolment memos) so a BLOCK selection shows that block's gender split,
    // not the whole district's.
    if (selectedSchool) {
      const sel = selectedSchool as unknown as Record<string, unknown>;
      const selId = Number(sel.school_id ?? 0);
      const selName = String(selectedSchool.name ?? '')
        .trim()
        .toLowerCase();
      rows = rows.filter((r) =>
        selId > 0
          ? Number(r.school_id) === selId
          : String(r.school_name ?? '')
              .trim()
              .toLowerCase() === selName
      );
    } else if (drillBlock) {
      rows = rows.filter((r) =>
        drillBlockId != null
          ? Number(r.block_id) === drillBlockId
          : String(r.block_name ?? '') === drillBlock
      );
    }
    if (rows.length === 0) return null;
    let b = 0;
    let g = 0;
    let t = 0;
    rows.forEach((r) => {
      b += num(r.total_b);
      g += num(r.total_g);
      t += num(r.total_t);
    });
    const total = b + g + t;
    if (total === 0) return null;
    // Transgender is ALWAYS included as its own slice/legend entry — matching
    // the state-level and single-school gender splits. It carries its real
    // count so the tooltip shows "Transgender: <count> (<pct>)"; a district
    // with none simply reads "0%" instead of the category disappearing.
    const slices: ChartSlice[] = [
      { label: 'Boys', value: asPct(b, total), color: '#f0803c', count: b },
      { label: 'Girls', value: asPct(g, total), color: '#26a69a', count: g },
      {
        label: 'Transgender',
        value: asPct(t, total),
        color: '#7e57c2',
        count: t,
      },
    ];
    return slices;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    drillDistrictId,
    drillChildCount,
    selectedSchool,
    drillBlock,
    drillBlockId,
    fltManagement,
    fltCategory,
    fltSchoolType,
  ]);

  // Gender slices: the district-scoped split (with real transgender) when a
  // district is drilled, else the normal dataset for the selected view.
  const genderSlices =
    genderView === 'gender' && districtGenderSplit
      ? districtGenderSplit
      : genderDatasets[genderView];

  // Transgender availability for the current selection (district / block /
  // school). Shown as a small note under the Gender Split chart: whether the
  // selected scope has transgender students, and the count when it does.
  // Additive — reads the already-built gender slices / school detail; the chart
  // itself is unchanged.
  const transgenderInfo = useMemo(() => {
    // School level — use the resolved single-school gender split.
    if (selectedSchool && schoolDetail) {
      const tg = schoolDetail.genderSplit?.find(
        (s) => s.label === 'Transgender'
      );
      const count = num(tg?.count);
      return {
        scope: headerSchool ?? selectedSchool.name ?? 'School',
        level: 'School',
        available: count > 0,
        count,
      };
    }
    // District / block level — use the drilled gender split.
    if (districtGenderSplit) {
      const tg = districtGenderSplit.find((s) => s.label === 'Transgender');
      const count = num(tg?.count);
      return {
        scope: headerBlock ?? headerDistrict ?? drillDistrict ?? 'Selection',
        level: drillBlock ? 'Block' : 'District',
        available: count > 0,
        count,
      };
    }
    return null;
  }, [
    selectedSchool,
    schoolDetail,
    districtGenderSplit,
    headerSchool,
    headerBlock,
    headerDistrict,
    drillDistrict,
    drillBlock,
  ]);

  const genderChart = useMemo(
    () => (
      <div className="gender-split-wrap">
        <AppChart
          type="doughnut"
          title="Gender Split"
          icon="fa-solid fa-venus-mars"
          iconColor="#7e57c2"
          slices={genderSlices}
          height={320}
        />
        {/* Transgender availability note for the current selection — sits
            inside the Gender Split card, below the chart. */}
        {/* {genderView === 'gender' && transgenderInfo && (
          <div
            className={`gender-tg-note ${
              transgenderInfo.available
                ? 'gender-tg-note--yes'
                : 'gender-tg-note--no'
            }`}
          >
            <i
              className={
                transgenderInfo.available
                  ? 'pi pi-check-circle'
                  : 'pi pi-info-circle'
              }
            />
            <span>
              {transgenderInfo.available
                ? `Transgender available — ${transgenderInfo.count.toLocaleString(
                    'en-IN'
                  )} student${transgenderInfo.count === 1 ? '' : 's'}`
                : 'No transgender students recorded'}
            </span>
          </div>
        )} */}
      </div>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [genderView, schoolDash, genderSlices, transgenderInfo]
  );

  // District/block/school-scoped Social Category from the studentchildcount
  // District/block/school-scoped Social Category from the studentchildcount
  // allyear drill rows (oc/bc/mbc/dnc/sc/st per school), filtered client-side
  // by the active classification — the SAME source the management & gender
  // donuts use, so Social Category also reflects the selected scope / filter.
  const districtSocialCategory = useMemo<ChartSlice[] | null>(() => {
    if (drillDistrictId == null) return null;
    let rows = applyClassFilter(
      (drillChildCount?.result ?? []) as Record<string, unknown>[]
    );
    if (selectedSchool) {
      const sel = selectedSchool as unknown as Record<string, unknown>;
      const selId = Number(sel.school_id ?? 0);
      const selName = String(selectedSchool.name ?? '')
        .trim()
        .toLowerCase();
      rows = rows.filter((r) =>
        selId > 0
          ? Number(r.school_id) === selId
          : String(r.school_name ?? '')
              .trim()
              .toLowerCase() === selName
      );
    } else if (drillBlock) {
      rows = rows.filter((r) =>
        drillBlockId != null
          ? Number(r.block_id) === drillBlockId
          : String(r.block_name ?? '') === drillBlock
      );
    }
    if (rows.length === 0) return null;

    const cats = [
      { label: 'OC', key: 'oc', color: '#f2c14e' },
      { label: 'BC', key: 'bc', color: '#26a69a' },
      { label: 'MBC', key: 'mbc', color: '#8bc34a' },
      { label: 'DNC', key: 'dnc', color: '#c62828' },
      { label: 'SC', key: 'sc', color: '#f0803c' },
      { label: 'ST', key: 'st', color: '#2f5fb0' },
    ];
    const totals = cats.map((c) => rows.reduce((s, r) => s + num(r[c.key]), 0));
    const grand = totals.reduce((a, b) => a + b, 0);
    if (grand === 0) return null;
    return cats.map((c, i) => ({
      label: c.label,
      value: asPct(totals[i], grand),
      color: c.color,
      count: totals[i],
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    drillDistrictId,
    drillChildCount,
    selectedSchool,
    drillBlock,
    drillBlockId,
    fltManagement,
    fltCategory,
    fltSchoolType,
  ]);

  // Social slices: the district-scoped breakdown when drilled, else the
  // state-level / filtered dataset for the selected view.
  const socialSlices =
    socialView === 'social' && districtSocialCategory
      ? districtSocialCategory
      : socialDatasets[socialView];

  const socialChart = useMemo(
    () => (
      <AppChart
        type="doughnut"
        title={`${
          socialViews.find((v) => v.value === socialView)?.label ??
          'Social Category'
        } — ${managementScopeLabel}`}
        icon="fa-solid fa-users-line"
        iconColor="#26a69a"
        // Use the scope-aware slices (district/block/school breakdown when
        // drilled), NOT the raw state-level dataset — this is why the donut
        // wasn't changing with the selection.
        slices={socialSlices}
        height={320}
      />
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [socialView, socialSlices, schoolDash, managementScopeLabel]
  );
  //console.log('schoolDash===================0', schoolDash);

  // Enrolment-by-grade data for the chart. Prefer the active scope's gender
  // split (schoolDash: drilled district / filtered). On first render that has
  // only totals, so fall back to the state-level live abstract
  // (schoolabstractallyear) which DOES carry real per-grade boys/girls.
  const stateEnrolment = useMemo(() => {
    const summary = stateAbstract?.result?.summary;
    return summary
      ? mapSchoolDashboard(summary as SchoolDashboardData)?.enrolmentByGrade
      : undefined;
  }, [stateAbstract]);

  // District/block/school-scoped Enrolment by Grade from the studentchildcount
  // allyear drill rows (prkg_b/prkg_g … c12_b/c12_g), filtered client-side by
  // the active Management / Category / School-Type selection — the SAME source
  // the management & gender donuts use. Lets the enrolment chart reflect the
  // filter (via studentchildcountallyear) just like the other donuts.
  const districtEnrolmentByGrade = useMemo(() => {
    if (drillDistrictId == null) return null;
    let rows = applyClassFilter(
      (drillChildCount?.result ?? []) as Record<string, unknown>[]
    );
    if (selectedSchool) {
      const sel = selectedSchool as unknown as Record<string, unknown>;
      const selId = Number(sel.school_id ?? 0);
      const selName = String(selectedSchool.name ?? '')
        .trim()
        .toLowerCase();
      rows = rows.filter((r) =>
        selId > 0
          ? Number(r.school_id) === selId
          : String(r.school_name ?? '')
              .trim()
              .toLowerCase() === selName
      );
    } else if (drillBlock) {
      rows = rows.filter((r) =>
        drillBlockId != null
          ? Number(r.block_id) === drillBlockId
          : String(r.block_name ?? '') === drillBlock
      );
    }
    if (rows.length === 0) return null;

    const gradeKeys = [
      ['Pre-KG', 'prkg'],
      ['LKG', 'lkg'],
      ['UKG', 'ukg'],
      ['I', 'c1'],
      ['II', 'c2'],
      ['III', 'c3'],
      ['IV', 'c4'],
      ['V', 'c5'],
      ['VI', 'c6'],
      ['VII', 'c7'],
      ['VIII', 'c8'],
      ['IX', 'c9'],
      ['X', 'c10'],
      ['XI', 'c11'],
      ['XII', 'c12'],
    ] as const;
    const boys = gradeKeys.map(([, k]) =>
      rows.reduce((s, r) => s + num(r[`${k}_b`]), 0)
    );
    const girls = gradeKeys.map(([, k]) =>
      rows.reduce((s, r) => s + num(r[`${k}_g`]), 0)
    );
    const trans = gradeKeys.map(([, k]) =>
      rows.reduce((s, r) => s + num(r[`${k}_t`]), 0)
    );
    const total = gradeKeys.map(([, k], i) => boys[i] + girls[i] + trans[i]);
    if (
      !boys.some((v) => v > 0) &&
      !girls.some((v) => v > 0) &&
      !total.some((v) => v > 0)
    ) {
      return null;
    }
    return { grades: gradeKeys.map(([l]) => l), boys, girls, trans, total };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    drillDistrictId,
    drillChildCount,
    selectedSchool,
    drillBlock,
    drillBlockId,
    fltManagement,
    fltCategory,
    fltSchoolType,
  ]);

  const enrolment = useMemo(() => {
    // ── Added: strict source-by-mode ──
    //  • Any filter active → API value only: the drilled district's filtered
    //    studentchildcountallyear split, else the filtered schoolabstractallyear
    //    (schoolDash). Never the unfiltered state fallback.
    //  • First render / no filter, no drill → ONLY the dashboardsummary JSON.
    if (hasAbstractFilter) {
      return districtEnrolmentByGrade ?? schoolDash?.enrolmentByGrade;
    }
    if (!districtEnrolmentByGrade && schoolDash?.enrolmentByGrade) {
      return schoolDash.enrolmentByGrade;
    }

    // ── Previous logic (unchanged, kept as fallback) ──
    // Drilled scope → the filtered studentchildcountallyear per-grade split.
    if (districtEnrolmentByGrade) return districtEnrolmentByGrade;
    const scoped = schoolDash?.enrolmentByGrade;
    //console.log(scoped, 'scoped');
    // If the current scope already has a real boys/girls split, use it.
    if (scoped && (scoped.boys ?? []).some((v) => v > 0)) return scoped;
    // Else prefer the state abstract's real per-grade split when available.
    if (stateEnrolment && (stateEnrolment.boys ?? []).some((v) => v > 0)) {
      return stateEnrolment;
    }
    // Else fall back to totals (single series).
    return scoped ?? stateEnrolment;
  }, [hasAbstractFilter, districtEnrolmentByGrade, schoolDash, stateEnrolment]);

  const enrolmentChart = useMemo(
    () => (
      <AppChart
        type="stackedBar"
        title={`Enrolment by Grade — ${managementScopeLabel}`}
        icon="pi pi-chart-bar"
        iconColor="#f0803c"
        // API data only — no dummy fallback (empty until the API responds).
        labels={enrolment?.grades ?? []}
        tools={[
          { icon: 'pi pi-refresh', label: 'Refresh' },
          { icon: 'pi pi-file', label: 'Table View' },
          { icon: 'pi pi-chart-bar', label: 'Bar view' },
          { icon: 'pi pi-chart-line', label: 'Line view' },
          // { icon: 'pi pi-clone', label: 'Stacked view' },
          { icon: 'pi pi-download', label: 'Export' },
        ]}
        series={
          // Real per-grade boys/girls split → stacked Boys/Girls. Otherwise a
          // single "Students" total series (no fabricated split).
          (enrolment?.boys ?? []).some((v) => v > 0)
            ? [
                {
                  label: 'Boys',
                  data: enrolment?.boys ?? [],
                  color: '#f0803c',
                },
                {
                  label: 'Girls',
                  data: enrolment?.girls ?? [],
                  color: '#26a69a',
                },
                // Transgender is always shown as its own bar/legend entry,
                // even when every grade's count is 0 (then it just reads 0).
                {
                  label: 'Transgender',
                  data: enrolment?.trans ?? [],
                  color: '#7e57c2',
                },
              ]
            : [
                {
                  label: 'Students',
                  data: enrolment?.total ?? [],
                  color: '#2f5fb0',
                },
              ]
        }
        yTitle="Students"
        yTickSuffix=""
        height={320}
      />
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [enrolment, managementScopeLabel]
  );

  // ── District-wise combo chart (Students / Schools) with a Share % line ──
  // Bars = the selected metric per district (sorted desc); line = each
  // district's share of the state total. Click a bar to drill into it.
  const [districtWiseMetric, setDistrictWiseMetric] = useState<
    'students' | 'schools'
  >('students');

  const districtWise = useMemo(() => {
    // Drive the chart from the SAME level-aware rows the grid shows
    // (reportView.data): districts → blocks → schools. So a bar click can
    // drill one level deeper (via handleReportRowClick), exactly like the grid
    // rows — the chart now drills all the way down to school level instead of
    // staying stuck on districts.
    const metricKey = districtWiseMetric; // 'students' | 'schools'
    const srcRows = (reportView.data ?? []) as Record<string, unknown>[];
    const mapped = srcRows
      .map((r) => ({
        row: r,
        name: String(r.name ?? ''),
        // 'schools' isn't present at the school leaf → fall back to students.
        raw: Number(r[metricKey] ?? r.students ?? 0),
      }))
      .sort((a, b) => b.raw - a.raw);
    const total = mapped.reduce((sum, m) => sum + m.raw, 0) || 1;
    return {
      // The clicked bar maps back to this row so the drill matches the grid.
      rows: mapped.map((m) => m.row),
      labels: mapped.map((m) => m.name),
      values: mapped.map((m) => m.raw),
      // Share % of the current level's total (state / district / block).
      shares: mapped.map((m) => Math.round((m.raw / total) * 1000) / 10),
    };
  }, [reportView, districtWiseMetric]);

  const districtWiseChart = useMemo(
    () => (
      <AppChart
        type="combo"
        title={`${
          reportView.level === 1
            ? 'District-wise'
            : reportView.level === 2
              ? `Blocks of ${drillDistrict ?? ''}`
              : `Schools of ${drillBlock ?? ''}`
        } ${districtWiseMetric === 'students' ? 'Students' : 'Schools'}${
          reportView.level === 3
            ? ' — click a bar to open the school'
            : ' — click a bar to drill down'
        }`}
        icon="fa-solid fa-chart-simple"
        iconColor="#2f5fb0"
        legendPosition="top"
        headerRight={
          <div className="dashboard-toggle">
            <button
              type="button"
              className={`dashboard-toggle__btn${
                districtWiseMetric === 'students' ? ' is-active' : ''
              }`}
              onClick={() => setDistrictWiseMetric('students')}
            >
              <i className="pi pi-users" /> Students
            </button>
            <button
              type="button"
              className={`dashboard-toggle__btn${
                districtWiseMetric === 'schools' ? ' is-active' : ''
              }`}
              onClick={() => setDistrictWiseMetric('schools')}
            >
              <i className="pi pi-building" /> Schools
            </button>
          </div>
        }
        tools={[
          { icon: 'pi pi-refresh', label: 'Refresh' },
          { icon: 'pi pi-file', label: 'Table View' },
          { icon: 'pi pi-chart-bar', label: 'Bar view' },
          { icon: 'pi pi-chart-line', label: 'Line view' },
          // { icon: 'pi pi-images', label: 'Stacked view' },
          { icon: 'pi pi-download', label: 'Export' },
        ]}
        labels={districtWise.labels}
        series={[
          {
            label: districtWiseMetric === 'students' ? 'Students' : 'Schools',
            data: districtWise.values,
            color: '#2f5fb0',
          },
          {
            label: 'Share %',
            data: districtWise.shares,
            color: '#f0803c',
            asLine: true,
            useRightAxis: true,
          },
        ]}
        yTitle={districtWiseMetric === 'students' ? 'Students' : 'Schools'}
        y1Title="Share %"
        y1TickSuffix="%"
        // Cap the right (Share %) axis at 50% instead of the default 100%.
        y1Max={50}
        // At school level there can be hundreds of schools — page the x-axis at
        // 20 bars per view with a ◀/▶ pager, so each slide stays readable.
        labelsPerPage={reportView.level === 3 ? 20 : undefined}
        // Click a bar → drill one level deeper using the SAME handler the grid
        // rows use, so the chart goes district → block → school and a school
        // bar opens its detail. `index` maps back to the sorted source row.
        onBarClick={(index) => {
          const row = districtWise.rows[index];
          if (row) handleReportRowClick(row);
        }}
        height={360}
      />
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      districtWise,
      districtWiseMetric,
      reportView.level,
      drillDistrict,
      drillBlock,
    ]
  );
  // Track viewport width so we can swap the table for a card layout on mobile.
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= 900 : false
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 900);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Which parameters the Filters component should attempt to build. Only the
  // keys that actually exist on the rows will render as filters.
  const filterParameters = [
    'district_id,district_name',
    'block_id,block_name',
    'No_Of_Centers',
    'Mapped_schools',
    'student_id,student_name', // not in data → skipped automatically
  ];

  const tableColumns = [
    { key: 'district_name', label: 'District' },
    { key: 'block_id', label: 'Block ID' },
    { key: 'block_name', label: 'Block Name' },
    { key: 'No_Of_Centers', label: 'No. Of Centers' },
    { key: 'Mapped_schools', label: 'Mapped Schools' },
    { key: 'Total_Students_Mapped', label: 'Total Students Mapped' },
  ];

  // Aggregate the (filtered) rows by district for the charts.
  const districtTotals = useMemo(() => {
    const totals = new Map<
      string,
      { centers: number; schools: number; students: number }
    >();
    filteredRows.forEach((row) => {
      const name = String(row.district_name ?? 'Unknown');
      const acc = totals.get(name) ?? { centers: 0, schools: 0, students: 0 };
      acc.centers += Number(row.No_Of_Centers ?? 0);
      acc.schools += Number(row.Mapped_schools ?? 0);
      acc.students += Number(row.Total_Students_Mapped ?? 0);
      totals.set(name, acc);
    });
    return Array.from(totals.entries()).map(([district, v]) => ({
      district,
      ...v,
    }));
  }, [filteredRows]);

  const palette = [
    '#313fc4',
    '#3b82f6',
    '#22c55e',
    '#f59e0b',
    '#ef4444',
    '#8b5cf6',
    '#06b6d4',
  ];

  // Shared inputs for the district charts (AppChart format).
  const districtLabels = useMemo(
    () => districtTotals.map((d) => d.district),
    [districtTotals]
  );

  // Compliance combo-chart series (Submitted/Pending bars + Compliance % line).
  const complianceLabels = complianceByPeriod.map((c) => c.period);
  const complianceSeries: ChartSeries[] = [
    {
      label: 'Submitted',
      data: complianceByPeriod.map((c) => c.submitted),
      color: '#4a9e5c',
    },
    {
      label: 'Pending',
      data: complianceByPeriod.map((c) => c.pending),
      color: '#d9584f',
    },
    {
      label: 'Compliance %',
      data: complianceByPeriod.map((c) => c.compliance),
      color: '#2f5fb0',
      asLine: true,
      useRightAxis: true,
    },
  ];

  // Sub-tabs shown in the header. "Trend Analysis" is only relevant to the
  // School Dashboard; Infrastructure hides it.
  const subTabs = useMemo(() => {
    const tabs = [
      { label: 'Numbers', value: 'numbers', icon: 'pi pi-calculator' },
      {
        label: 'Trend Analysis',
        value: 'trend',
        icon: 'fa-solid fa-chart-line',
      },
      {
        label: 'Comparison',
        value: 'comparison',
        icon: 'fa-solid fa-code-compare',
      },
      // {
      //   label: 'Alarming',
      //   value: 'alarming',
      //   icon: 'pi pi-exclamation-triangle',
      //   count: 22,
      //   tone: 'danger' as const,
      // },
      {
        label: 'Caution',
        value: 'caution',
        icon: 'fa-solid fa-circle-exclamation',
        // Count hidden for now (uncomment to show the flagged-school total).
        // count: cautionTotal,
        // tone: 'warning' as const,
      },
    ];
    // Hide all subtabs on Review Dashboard
    if (activeNav === 'review-dashboard') return [];
    // Hide Trend Analysis on Infrastructure
    return activeNav === 'infrastructure'
      ? tabs.filter((t) => t.value !== 'trend')
      : tabs;
  }, [activeNav, cautionTotal]);

  // If the active sub-tab disappears for the current nav (e.g. switching to
  // Infrastructure while "Trend Analysis" is selected), fall back to Numbers.
  useEffect(() => {
    if (!subTabs.some((t) => t.value === activeSubTab)) {
      setActiveSubTab('numbers');
    }
  }, [subTabs, activeSubTab]);

  if (!s.loaded) {
    return (
      <main className="app-content p-3">
        <p className="fs-6 text-muted">Loading session…</p>
      </main>
    );
  }

  // The DistrictMap panel, extracted once so it can render in BOTH the normal
  // (district/block) layout AND beside the single-school detail view. It is
  // gated by `showMap` so the Open/Close Map button is the only thing that
  // toggles the map, and the map stays visible by default at every drill level
  // (district, block, school). Returns null when the map is closed.
  const mapPanel = showMap ? (
    <div className="col-12 col-lg-5">
      <DistrictMap
        title={
          mapMetrics.find((m) => m.value === mapMetric)?.label ??
          'Total Students'
        }
        metrics={mapMetrics}
        metric={mapMetric}
        onMetricChange={setMapMetric}
        data={mapData}
        unitSuffix={
          mapMetric === 'total-schools' || mapMetric === 'teachers' ? '' : 'L'
        }
        onClose={() => setShowMap(false)}
        onDistrictClick={(clickedName) => {
          // Resolve the clicked map name (UPPERCASE) to the exact master
          // district name via a normalized match, so it lines up with the
          // dropdown's option values.
          const nk = (sv: string) => sv.trim().toUpperCase();
          const master = districtOptions.find(
            (o) => nk(o.value) === nk(clickedName)
          )?.value;
          if (!master) return;
          // Toggle: clicking the already-selected district clears the selection
          // (map + dropdown); a different district selects it.
          // handleDistrictChange keeps headerDistrict and drillDistrict (hence
          // the map highlight) in sync.
          const current = headerDistrict ?? drillDistrict;
          const same = !!current && nk(current) === nk(master);
          handleDistrictChange(same ? '' : master);
        }}
        // Highlight the currently-scoped district on the map and clear it when
        // deselected (#18/#49).
        selectedDistrictName={headerDistrict ?? drillDistrict}
        height={560}
      />
    </div>
  ) : null;

  return (
    <main className="app-content" ref={contentRef}>
      {/* <div className="row g-3">
        {sections.map(({ title, rows }) => (
          <div className="col-md-6 col-xl-4" key={title}>
            <div className="card h-100">
              <div className="card-header fw-semibold py-2">{title}</div>
              <div className="card-body p-0">
                <table className="table table-sm table-bordered mb-0">
                  <tbody>
                    {rows.map(([label, value]) => (
                      <tr key={label}>
                        <td
                          className="text-muted text-nowrap ps-2 pe-3"
                          style={{ width: '45%' }}
                        >
                          {label}
                        </td>
                        <td className="fw-semibold">{value}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        ))}
      </div> */}
      {/* ── Dashboard header ─────────────────────────── */}
      <DashboardHeader
        date={todayLabel}
        // Review Dashboard, Attendance and Infrastructure are frozen to the
        // current academic year only (yearFrozenNav / currentYearOnlyOptions
        // above) — the dropdown shows just that one year and is disabled.
        // It's hidden entirely only on the Trend Analysis sub-tab of School
        // Dashboard / Academic Scores, which has its own FROM/TO year-range
        // controls — a single header year is redundant there.
        years={
          yearFrozenNav
            ? currentYearOnlyOptions
            : (activeNav === 'school-dashboard' ||
                  activeNav === 'academic-scores') &&
                activeSubTab === 'trend'
              ? []
              : yearOptions
        }
        year={effectiveHeaderYear}
        onYearChange={handleYearChange}
        yearDisabled={yearFrozenNav}
        userName={s.name}
        designation={s.designation}
        onUserClick={handleLogout}
        logoSrc={govtLogo}
        department="School Education Department"
        title="Government of Tamil Nadu"
        subtitle="State Education Portal"
        navTabs={[
          // Review Dashboard is still being validated in production —
          // uncomment this line to show its tab again for everyone once
          // it's signed off. Until then it's still reachable for testing via
          // /dashboard?preview=review-dashboard (see activeNav above).
          { label: 'Review Dashboard', value: 'review-dashboard' },
          { label: 'School Dashboard', value: 'school-dashboard' },
          { label: 'Infrastructure', value: 'infrastructure' },
          { label: 'Academic Scores', value: 'academic-scores' },
          { label: 'Attendance', value: 'attendance' },
          { label: 'SLAS', value: 'slas', external: true },
          { label: 'SMC', value: 'smc', external: true },
        ]}
        activeNav={activeNav}
        onNavChange={handleNavChange}
        actions={[
          // "Open Map" is only relevant on the School Dashboard → Numbers
          // sub-tab (not Trend / Comparison / Caution).
          ...(activeNav === 'school-dashboard' && activeSubTab === 'numbers'
            ? [
                {
                  label: !showMap ? 'Open Map' : 'Close Map',
                  icon: !showMap ? 'pi pi-map' : 'pi pi-times',
                  variant: (showMap ? 'primary' : 'outlined') as
                    'primary' | 'outlined',
                  onClick: () => setShowMap((v) => !v),
                },
              ]
            : []),
          {
            label: 'Filter',
            icon: 'pi pi-filter',
            // Active (solid blue) when the filters row is open, otherwise a
            // white button with blue border/text.
            variant: showFilters ? 'primary' : 'toggle',
            // Count badge = total active Management + Category + School-Type
            // selections, so the active filters are visible even when the row
            // is collapsed.
            badge:
              fltManagement.length + fltCategory.length + fltSchoolType.length,
            onClick: () => setShowFilters((v) => !v),
          },
          {
            label: exportingPdf ? 'Exporting…' : 'Export PDF',
            icon: exportingPdf ? 'pi pi-spin pi-spinner' : 'pi pi-download',
            variant: 'solid',
            onClick: handleExportPdf,
          },
        ]}
        breadcrumb="Tamil Nadu"
        districts={districtOptions}
        district={headerDistrict}
        onDistrictChange={handleDistrictChange}
        districtLocked={districtLocked}
        // On a caution tab, the Block list is scoped to blocks that actually
        // have caution records (reported by the Caution component); elsewhere
        // it's the full block list for the district. BUT that scoped list is
        // derived from the currently-active check/tile's flagged-school list,
        // which is empty whenever that one check has zero schools (e.g.
        // Caution's default "Zero Enrolment" check is commonly 0) — which
        // used to collapse the Block dropdown to nothing, hiding it entirely
        // even with a block selected. Fall back to the full district block
        // list when the caution scope has no blocks, so the Block dropdown
        // (pure navigation) never disappears.
        blocks={
          cautionScope && cautionScope.blocks.length > 0
            ? cautionScope.blocks
            : blockOptions
        }
        block={headerBlock}
        onBlockChange={handleBlockChange}
        blockLocked={blockLocked}
        // The School dropdown is available on EVERY nav tab and sub-tab. It
        // used to be suppressed on Infrastructure and on Attendance's
        // non-Numbers sub-tabs, which made the header inconsistent: the
        // selection survived a tab switch but the control to change or clear
        // it vanished. Tabs that only render down to block level simply ignore
        // the school scope. On a caution tab the list is scoped to schools
        // with caution records.
        // Review Dashboard drills only to the block level, so its header never
        // shows a School dropdown (empty list suppresses it).
        // Same fallback as the Block list above: the caution scope's school
        // list is the active check/tile's flagged schools, which is empty
        // when that one check has zero schools (e.g. Caution's default
        // "Zero Enrolment") — which hid the School dropdown entirely. Fall
        // back to the block's full school list so the dropdown stays usable;
        // the caution views auto-drop a carried school that doesn't apply on
        // a check/tile change (not on a deliberate pick), so this doesn't
        // bounce.
        schools={
          cautionScope && cautionScope.schools.length > 0
            ? cautionScope.schools
            : schoolOptions
        }
        school={headerSchool}
        schoolCrumb={
          headerSchool ? withUdise(headerSchool, headerSchoolUdise) : null
        }
        onSchoolChange={handleSchoolChange}
        programmeFilter={
          isReviewTab && showFilters
            ? {
                value: fltProgramme,
                options: programmeOptions,
                onChange: setFltProgramme,
              }
            : undefined
        }
        filters={
          showFilters
            ? [
                // Category / School Type / Management are shown on every tab
                // now. On the official Palli Paarvai tabs (1 / 3) they're
                // frozen (disabled) because classification doesn't apply to
                // that data — see freezeClassFilters above.
                {
                  key: 'category',
                  label: 'CATEGORY',
                  value: fltCategory,
                  placeholder: 'All Categories',
                  options: categoryOptions,
                  onChange: setFltCategory,
                  disabled: freezeClassFilters,
                },
                {
                  key: 'schoolType',
                  label: 'SCHOOL TYPE',
                  value: fltSchoolType,
                  placeholder: 'All School Types',
                  options: schoolTypeOptions,
                  onChange: (v: string[]) => {
                    setFltSchoolType(v);
                    setFltManagement([]); // reset management whenever type changes
                  },
                  disabled: freezeClassFilters,
                },
                // Management appears after a School Type is selected — and is
                // also shown (frozen) on the official tabs so all three are
                // visible together there.
                ...(fltSchoolType.length > 0 || freezeClassFilters
                  ? [
                      {
                        key: 'management',
                        label: 'MANAGEMENT',
                        value: fltManagement,
                        placeholder: 'All Management',
                        options: managementOptions,
                        onChange: setFltManagement,
                        disabled: freezeClassFilters,
                      },
                    ]
                  : []),
              ]
            : []
        }
        onClearFilters={clearHeaderFilters}
        subTabs={subTabs}
        activeSubTab={activeSubTab}
        onSubTabChange={setActiveSubTab}
        // sources={[
        //   { label: 'SOURCE', value: 'source' },
        //   { label: 'EMIS', value: 'emis' },
        //   { label: 'UDISE+', value: 'udise' },
        // ]}
        source={headerSource}
        onSourceChange={setHeaderSource}
        className="mb-3"
        cardInPalliPaarvai={cardInPalliPaarvai}
        // Frozen on the Review Dashboard components (cards) grid — selecting a
        // school has no effect there. Also disabled on Palli Paarvai by default
        // (most of its tabs have no school-level grid), EXCEPT Schools Not
        // Observed (2) / Schools 3+ Times (4), which do and support picking one.
        schoolDisabled={
          reviewComponentsView ||
          (cardInPalliPaarvai === 'Palli Paarvai' &&
            palliPaarvaiViewId !== 2 &&
            palliPaarvaiViewId !== 4)
        }
      />

      {activeNav === 'review-dashboard' ? (
        <ReviewDashboard
          selectedProgramme={fltProgramme}
          selectedManagement={fltManagement}
          selectedCategory={fltCategory}
          selectedSchoolType={fltSchoolType}
          onGeoSync={handleReviewGeoSync}
          headerDistrict={headerDistrict}
          headerBlock={headerBlock}
          headerDistrictId={selectedDistrictId ?? null}
          headerBlockId={selectedBlockId ?? null}
          headerSchool={headerSchool}
          headerSchoolUdise={headerSchoolUdise}
          onSchoolChange={handleSchoolChange}
          onSchoolSelect={handleReviewSchoolSelect}
          activeDetail={reviewDetailCard}
          onDetailChange={setReviewDetailCard}
          setCardInPalliPaarvai={setCardInPalliPaarvai}
          setPalliPaarvaiViewId={setPalliPaarvaiViewId}
          // Forwarded straight through to PalliPaarvaiDetail's own
          // onGeoSelect (district + block + school set together, correctly
          // — unlike onGeoSync, handleGeoSelect doesn't drop `school`).
          onGeoSelect={handleGeoSelect}
          // Reuses the SAME cautionScope state Infrastructure's Caution tab
          // uses — only one Caution-style view (or, here, Palli Paarvai's
          // schools grid) is ever mounted/active at a time, so whichever one
          // narrows the header Block/School dropdowns, reverting to the
          // full lists on its own unmount/drill-level change.
          onScopeOptions={setCautionScope}
          currentAcdYr={selectedAcdYr}
          districtLocked={districtLocked}
          blockLocked={blockLocked}
        />
      ) : activeNav === 'academic-scores' ? (
        <AcademicScores
          year={selectedYearLabel}
          activeSubTab={activeSubTab}
          acdYr={selectedAcdYr}
          districtId={selectedDistrictId ?? null}
          blockId={selectedBlockId ?? null}
          schoolId={selectedSchoolId ?? null}
          district={headerDistrict}
          block={headerBlock}
          school={headerSchool}
          filters={drillFilters}
          onDistrictChange={handleDistrictChange}
          onBlockChange={handleBlockChange}
          onSchoolChange={(v) => setHeaderSchool(v || null)}
          onGeoSelect={handleGeoSelect}
          // Caution sub-tab narrows the header Block/School dropdowns to the
          // flagged schools in its grid — same shared cautionScope state the
          // other tabs' Caution views use.
          onScopeOptions={setCautionScope}
        />
      ) : activeNav === 'attendance' ? (
        <Attendance
          activeSubTab={activeSubTab}
          district={headerDistrict}
          block={headerBlock}
          school={headerSchool}
          districtId={selectedDistrictId ?? null}
          blockId={selectedBlockId ?? null}
          schoolId={selectedSchoolId ?? null}
          acdYr={selectedAcdYr}
          filters={drillFilters}
          onDistrictChange={handleDistrictChange}
          onBlockChange={handleBlockChange}
          onSchoolChange={(v) => setHeaderSchool(v || null)}
          onGeoSelect={handleGeoSelect}
          // Caution sub-tab narrows the header Block/School dropdowns to the
          // flagged schools in its grid — same shared cautionScope state the
          // other tabs' Caution views use.
          onScopeOptions={setCautionScope}
        />
      ) : activeNav === 'infrastructure' ? (
        <Infrastructure
          activeSubTab={activeSubTab}
          acdYr={selectedAcdYr}
          districtId={selectedDistrictId ?? null}
          blockId={selectedBlockId ?? null}
          district={headerDistrict}
          block={headerBlock}
          school={headerSchool}
          schoolUdise={headerSchoolUdise}
          onDistrictChange={handleDistrictChange}
          onBlockChange={handleBlockChange}
          onSchoolChange={(v) => setHeaderSchool(v || null)}
          onGeoSelect={handleGeoSelect}
          filters={drillFilters}
          // Reuses the SAME cautionScope state the other tabs' Caution view
          // uses — only one Caution-style view is ever mounted at a time, so
          // whichever one is active narrows the header Block/School dropdowns,
          // and its own unmount (tab switch) resets it back to the full lists.
          onScopeOptions={setCautionScope}
        />
      ) : activeSubTab === 'trend' ? (
        <TrendAnalysis
          districtId={selectedDistrictId ?? null}
          blockId={selectedBlockId ?? null}
          schoolId={selectedSchoolId ?? null}
          district={headerDistrict}
          block={headerBlock}
          school={headerSchool}
          acdYr={selectedAcdYr ?? null}
          filters={drillFilters}
          onDistrictChange={handleDistrictChange}
          onBlockChange={handleBlockChange}
          onSchoolChange={(v) => setHeaderSchool(v || null)}
        />
      ) : activeSubTab === 'comparison' ? (
        <Comparison
          rows={
            reportView.data as unknown as {
              name: string;
              schools?: number;
              boys: number;
              girls: number;
              students: number;
              teachers: number;
            }[]
          }
          level={reportView.level}
          year={selectedYearLabel}
          scopeLabel={
            reportView.level === 3
              ? `School Comparison — ${drillBlock ?? ''}`
              : reportView.level === 2
                ? `Block Comparison — ${drillDistrict ?? ''}`
                : 'District Comparison'
          }
          district={headerDistrict}
          block={headerBlock}
          school={headerSchool}
          schoolId={selectedSchoolId ?? null}
          onRowClick={handleReportRowClick}
          onDistrictChange={handleDistrictChange}
          onBlockChange={handleBlockChange}
        />
      ) : activeSubTab === 'caution' ? (
        <Caution
          acdYr={selectedAcdYr ?? null}
          districtId={selectedDistrictId ?? null}
          blockId={selectedBlockId ?? null}
          schoolId={selectedSchoolId ?? null}
          district={headerDistrict}
          block={headerBlock}
          school={headerSchool}
          filters={drillFilters}
          onDistrictChange={handleDistrictChange}
          onBlockChange={handleBlockChange}
          onSchoolChange={(v) => setHeaderSchool(v || null)}
          onScopeOptions={setCautionScope}
          onGeoSelect={handleGeoSelect}
        />
      ) : (
        <>
          {/* When Open Map is active, keep the overview layout (map + charts)
              visible for a selected SCHOOL too, so the map highlights the
              school's parent district and shows its stats — instead of
              switching to the single-school detail view. */}
          {schoolDetail ? (
            // A selected school ALWAYS shows its full School Details page. The
            // map is auto-closed on school selection (see the effect that calls
            // setShowMap(false) when selectedSchool is set), so this renders
            // full width without a squished map beside it.
            <SchoolDetailView
              detail={schoolDetail}
              blockName={drillBlock ?? ''}
              districtName={drillDistrict ?? ''}
              year={selectedYearLabel}
              onBack={() => setSelectedSchool(null)}
              onDistrictChange={handleDistrictChange}
              onBlockChange={handleBlockChange}
              // Use handleSchoolChange (not the bare setHeaderSchool) so
              // clicking the Block crumb clears BOTH headerSchool AND
              // selectedSchool — otherwise selectedSchool lingered, schoolDetail
              // stayed non-null, and the view never returned to the block's
              // school list.
              onSchoolChange={handleSchoolChange}
            />
          ) : entityDetail && !showMap ? (
            <EntityDetailView
              // Override the seeded-mock donut/series fields with the REAL
              // API-scoped data (same memos the map-open grid uses), so the
              // detail view matches the grid instead of showing mock values.
              detail={{
                ...entityDetail,
                managementWise: (districtManagementWise ??
                  entityDetail.managementWise) as unknown as typeof entityDetail.managementWise,
                genderSplit: (districtGenderSplit ??
                  entityDetail.genderSplit) as unknown as typeof entityDetail.genderSplit,
                socialCategory: (districtSocialCategory ??
                  entityDetail.socialCategory) as unknown as typeof entityDetail.socialCategory,
                enrolmentByGrade: (districtEnrolmentByGrade ??
                  entityDetail.enrolmentByGrade) as typeof entityDetail.enrolmentByGrade,
              }}
              year={selectedYearLabel}
              backLabel={
                drillBlock
                  ? `Back to ${drillDistrict} — Blocks`
                  : 'Back to Tamil Nadu — Districts'
              }
              homeLabel="Tamil Nadu"
              onBack={reportGoBack}
            />
          ) : (
            <>
              {/* ── KPI stat tiles (from dashboardsummary API, mock fallback) ── */}
              <div className="row g-3 mb-3 dash-section">
                <div className="col-6 col-md-4 col-xl">
                  <StatTile
                    icon="fa-solid fa-school"
                    value={dispKpi ? formatIndian(dispKpi.schools) : '59,312'}
                    subValue={
                      dispKpi ? formatIndian(dispKpi.schools) : '59,312'
                    }
                    label="Total Schools"
                  />
                </div>
                <div className="col-6 col-md-4 col-xl">
                  <StatTile
                    icon="fa-solid fa-user-graduate"
                    value={dispKpi ? formatShort(dispKpi.students) : '1.22 Cr'}
                    subValue={
                      dispKpi ? formatIndian(dispKpi.students) : '1,21,92,051'
                    }
                    label="Total Students"
                  />
                </div>
                <div className="col-6 col-md-4 col-xl">
                  <StatTile
                    icon="fa-solid fa-person"
                    value={dispKpi ? formatShort(dispKpi.boys) : '62.26 L'}
                    subValue={
                      dispKpi ? formatIndian(dispKpi.boys) : '62,25,508'
                    }
                    label="Boys"
                  />
                </div>
                <div className="col-6 col-md-4 col-xl">
                  <StatTile
                    icon="fa-solid fa-person-dress"
                    value={dispKpi ? formatShort(dispKpi.girls) : '59.67 L'}
                    subValue={
                      dispKpi ? formatIndian(dispKpi.girls) : '59,66,542'
                    }
                    label="Girls"
                  />
                </div>
                <div className="col-6 col-md-4 col-xl">
                  <StatTile
                    icon="fa-solid fa-chalkboard-user"
                    value={dispKpi ? formatShort(dispKpi.teachers) : '5.L'}
                    subValue={
                      dispKpi ? formatIndian(dispKpi.teachers) : '5,71,185'
                    }
                    label="Teachers"
                  />
                </div>
                <div className="col-6 col-md-4 col-xl">
                  <StatTile
                    variant="solid"
                    value={managementScopeLabel}
                    label={`${selectedYearLabel} · ${scopeBreakdownLabel}`}
                    note="Source: EMIS"
                  />
                </div>
              </div>

              {/* ── Secondary metrics ────────────────────────── */}
              <div className="row g-3 mb-4 dash-section">
                <div className="col-6 col-lg-3">
                  <MetricCard
                    value={dispMetrics ? dispMetrics.gpi : '0.96'}
                    label="Gender Parity Index"
                    accent="#22c55e"
                  />
                </div>
                <div className="col-6 col-lg-3">
                  <MetricCard
                    value={dispMetrics ? dispMetrics.ptr : '21.3 : 1'}
                    label="Pupil Teacher Ratio"
                  />
                </div>
                <div className="col-6 col-lg-3">
                  <MetricCard
                    value={dispMetrics ? dispMetrics.girlsShare : '48.9%'}
                    label="Girls Share"
                    accent="#f0803c"
                  />
                </div>
                <div className="col-6 col-lg-3">
                  <MetricCard
                    value={dispMetrics ? dispMetrics.avgStudents : '206'}
                    label="Avg Students / School"
                  />
                </div>
              </div>

              {/* ── Map (Open Map) + overview charts ── */}
              <div className="row g-3 mb-4 dash-section">
                {mapPanel}

                {/* When the map is open the charts pack into the right half (2×2);
            otherwise they span 4-across. */}
                <div className={showMap ? 'col-12 col-lg-7' : 'col-12'}>
                  <div className="row g-3">
                    <div
                      className={
                        showMap ? 'col-12 col-md-6' : 'col-12 col-md-6 col-xl-3'
                      }
                    >
                      {managementChart}
                    </div>
                    <div
                      className={
                        showMap ? 'col-12 col-md-6' : 'col-12 col-md-6 col-xl-3'
                      }
                    >
                      {genderChart}
                    </div>
                    <div
                      className={
                        showMap ? 'col-12 col-md-6' : 'col-12 col-md-6 col-xl-3'
                      }
                    >
                      {socialChart}
                    </div>
                    <div
                      className={
                        showMap ? 'col-12 col-md-6' : 'col-12 col-md-6 col-xl-3'
                      }
                    >
                      {enrolmentChart}
                    </div>
                  </div>
                </div>
              </div>

              {/* ── District-wise Students / Schools (click a bar to drill) ── */}
              <div className="mb-4 dash-section">{districtWiseChart}</div>
            </>
          )}

          {/* ── Drill-down report table (hidden at the deepest, school level) ── */}
          {!schoolDetail && (
            <div className="mb-4 dash-section">
              {/* The report table drills on its own state (drillDistrict /
                  drillBlock), so the trail reads from that and Back reuses
                  reportGoBack, which also keeps the header dropdowns in sync. */}
              <DrillBreadcrumb
                district={drillDistrict}
                block={drillBlock}
                onBack={reportGoBack}
                onDistrictChange={handleDistrictChange}
                onBlockChange={handleBlockChange}
              />
              <ReportDataTable
                key={reportView.title}
                title={reportView.title}
                hint={reportView.hint}
                icon="fa-solid fa-table-list"
                data={reportView.data}
                exportFileName={reportView.exportFileName}
                searchPlaceholder={reportView.searchPlaceholder}
                rows={12}
                // columns={reportView.columns}
                columns={
                  reportView.level === 3 && reportView.data.length === 1
                    ? [
                        { ...reportView.columns[0], type: 'text' },
                        ...reportView.columns.slice(1),
                      ]
                    : reportView.columns
                }
                onRowClick={handleReportRowClick}
                first={reportFirst}
                onPage={setReportFirst}
              />
            </div>
          )}
        </>
      )}
    </main>
  );
};

export default Dashboard;
